/**
 * Mock data provider for the tutorial sandbox (demo mode).
 *
 * Maps API URL patterns to static GET responses so that tutorials
 * can run without hitting the real backend. Each entry uses a regex
 * to match the request URL and returns mock data when matched.
 *
 * The dataset uses realistic French agro-food industry data with
 * stable UUIDs so that cross-entity references remain consistent.
 *
 * Add new entries here as tutorials cover more features.
 */

export interface MockRouteEntry {
  /** Regex applied against the request URL pathname */
  pattern: RegExp;
  /** The JSON body to return (or a factory that receives regex match and progress) */
  data: unknown | ((match: RegExpMatchArray, progress: number) => unknown);
}

// ---------------------------------------------------------------------------
// Stable UUIDs
// ---------------------------------------------------------------------------

// Articles
const ART_FARINE    = 'a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d';
const ART_BEURRE    = 'b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e';
const ART_SUCRE     = 'c3d4e5f6-a7b8-4c9d-0e1f-2a3b4c5d6e7f';
const ART_OEUFS     = 'd4e5f6a7-b8c9-4d0e-1f2a-3b4c5d6e7f8a';
const ART_LAIT      = 'e5f6a7b8-c9d0-4e1f-2a3b-4c5d6e7f8a9b';
const ART_BISCUITS  = 'f6a7b8c9-d0e1-4f2a-3b4c-000000000001';

// ArticleInfos (1:1 with Article)
const AI_FARINE     = 'f6a7b8c9-d0e1-4f2a-3b4c-5d6e7f8a9b0c';
const AI_BEURRE     = 'a7b8c9d0-e1f2-4a3b-4c5d-6e7f8a9b0c1d';
const AI_SUCRE      = 'b8c9d0e1-f2a3-4b4c-5d6e-7f8a9b0c1d2e';
const AI_OEUFS      = 'c9d0e1f2-a3b4-4c5d-6e7f-8a9b0c1d2e3f';
const AI_LAIT       = 'd0e1f2a3-b4c5-4d6e-7f8a-9b0c1d2e3f4a';
const AI_BISCUITS   = 'e1f2a3b4-c5d6-4e7f-8a9b-000000000001';

// Tiers
const TIERS_MOULIN  = '11111111-aaaa-4bbb-cccc-dddddddddddd';
const TIERS_BOULANG = '22222222-aaaa-4bbb-cccc-dddddddddddd';
const TIERS_SUPER   = '33333333-aaaa-4bbb-cccc-dddddddddddd';

// Adresses
const ADR_ENTREPOT  = '33333333-aaaa-4bbb-cccc-dddddddddddd';
const ADR_MOULIN    = '44444444-aaaa-4bbb-cccc-dddddddddddd';
const ADR_BOULANG   = '55555555-aaaa-4bbb-cccc-dddddddddddd';
const ADR_SUPER     = '66666666-aaaa-4bbb-cccc-dddddddddddd';

// Lots
const LOT_FARINE    = '77777777-aaaa-4bbb-cccc-dddddddddddd';
const LOT_BEURRE    = '88888888-aaaa-4bbb-cccc-dddddddddddd';
const LOT_SUCRE     = '99999999-aaaa-4bbb-cccc-dddddddddddd';
const LOT_OEUFS     = 'aaaaaaaa-aaaa-4bbb-cccc-dddddddddddd';
const LOT_LAIT      = 'bbbbbbbb-aaaa-4bbb-cccc-dddddddddddd';
const LOT_BISCUITS  = 'cccccccc-aaaa-4bbb-cccc-dddddddddddd';

// Commandes fournisseur
const CF_001        = 'cf000001-aaaa-4bbb-cccc-dddddddddddd';
const CF_002        = 'cf000002-aaaa-4bbb-cccc-dddddddddddd';
const CF_003        = 'cf000003-aaaa-4bbb-cccc-dddddddddddd';

// Commandes client
const CC_001        = 'cc000001-aaaa-4bbb-cccc-dddddddddddd';
const CC_002        = 'cc000002-aaaa-4bbb-cccc-dddddddddddd';

// Lignes commande fournisseur
const CFL_001       = 'cfl00001-aaaa-4bbb-cccc-dddddddddddd';
const CFL_002       = 'cfl00002-aaaa-4bbb-cccc-dddddddddddd';
const CFL_003       = 'cfl00003-aaaa-4bbb-cccc-dddddddddddd';

// Unités d'expédition (types)
const UET_001       = 'uet00001-aaaa-4bbb-cccc-dddddddddddd';

// Palettes (UE créées)
const PAL_001       = 'pal00001-aaaa-4bbb-cccc-dddddddddddd';

// Chargement + BL (tuto livraison)
const CHARG_001     = 'chg00001-aaaa-4bbb-cccc-dddddddddddd';
const BL_001        = 'bl000001-aaaa-4bbb-cccc-dddddddddddd';
const TIERS_TRANSP  = 'transp01-aaaa-4bbb-cccc-dddddddddddd';

// Lignes commande client
const CCL_001       = 'ccl00001-aaaa-4bbb-cccc-dddddddddddd';
const CCL_002       = 'ccl00002-aaaa-4bbb-cccc-dddddddddddd';
const CCL_003       = 'ccl00003-aaaa-4bbb-cccc-dddddddddddd';
const CCL_004       = 'ccl00004-aaaa-4bbb-cccc-dddddddddddd';

// Nomenclature
const NOM_BISCUITS  = 'nom00001-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_FARINE  = 'noml0001-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_BEURRE  = 'noml0002-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_SUCRE   = 'noml0003-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_OEUFS   = 'noml0004-aaaa-4bbb-cccc-dddddddddddd';
const NOM_L_LAIT    = 'noml0005-aaaa-4bbb-cccc-dddddddddddd';

// Process etapes
const PE_MELANGE    = 'pe000001-aaaa-4bbb-cccc-dddddddddddd';
const PE_INCORP_B   = 'pe000002-aaaa-4bbb-cccc-dddddddddddd';
const PE_AJOUT_OL   = 'pe000003-aaaa-4bbb-cccc-dddddddddddd';
const PE_REPOS      = 'pe000004-aaaa-4bbb-cccc-dddddddddddd';
const PE_FACONNAGE  = 'pe000005-aaaa-4bbb-cccc-dddddddddddd';
const PE_CUISSON    = 'pe000006-aaaa-4bbb-cccc-dddddddddddd';
const PE_REFROID    = 'pe000007-aaaa-4bbb-cccc-dddddddddddd';
const PE_CONDITION  = 'pe000008-aaaa-4bbb-cccc-dddddddddddd';

// Controles qualite
const CTRL_TEMP     = 'ctrl0001-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_ASPECT   = 'ctrl0002-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_POIDS    = 'ctrl0003-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_ETIQ     = 'ctrl0004-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_HOMOG    = 'ctrl0005-aaaa-4bbb-cccc-dddddddddddd';
const CTRL_POD      = 'cpod0001-aaaa-4bbb-cccc-dddddddddddd';
const POD_MELANGE   = 'cpod0002-aaaa-4bbb-cccc-dddddddddddd';
const POD_CUISSON   = 'cpod0003-aaaa-4bbb-cccc-dddddddddddd';
const POD_CONDITION = 'cpod0004-aaaa-4bbb-cccc-dddddddddddd';

// Pods contrôles spécif (réception lait, début opé biscuits)
const POD_RECEP_LAIT = 'cpod0010-aaaa-4bbb-cccc-dddddddddddd';
const POD_STARTOPE_BISC = 'cpod0011-aaaa-4bbb-cccc-dddddddddddd';

// Ligne de production
const LIGNE_BISC    = 'lig00001-aaaa-4bbb-cccc-dddddddddddd';
const LIGNE_PARAM   = 'lgp00001-aaaa-4bbb-cccc-dddddddddddd';

// Ordre de fabrication
const OF_BISCUITS   = 'of000001-aaaa-4bbb-cccc-dddddddddddd';

// SpecificationEtape for Biscuits
const SE_BISCUITS   = 'se000001-aaaa-4bbb-cccc-dddddddddddd';

// SpecificationEtapeMaster IDs (1 per article)
const SEM_IDS = [
  'a0a0a0a0-0001-4000-a000-000000000001',
  'a0a0a0a0-0001-4000-a000-000000000002',
  'a0a0a0a0-0001-4000-a000-000000000003',
  'a0a0a0a0-0001-4000-a000-000000000004',
  'a0a0a0a0-0001-4000-a000-000000000005',
  'a0a0a0a0-0001-4000-a000-000000000006',
];

// CustomEnums
const ENUM_FAM_MP   = 'enu00001-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_FAM_PF   = 'enu00002-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_UNIT_KG  = 'enu00003-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_UNIT_L   = 'enu00004-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_UNIT_U   = 'enu00005-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_FAM_TECH = 'enu00006-aaaa-4bbb-cccc-dddddddddddd';
const ENUM_SOUS_FAM_BISC = 'enu00007-aaaa-4bbb-cccc-dddddddddddd';

// ---------------------------------------------------------------------------
// Custom Enums
// ---------------------------------------------------------------------------

const customEnums = [
  {
    id: ENUM_FAM_MP,
    denomination: 'Matieres premieres',
    type: 'FAMILLE_ARTICLE',
    uniteRef: null,
    coefficient: 1,
    parentId: null,
    isActive: true,
  },
  {
    id: ENUM_FAM_PF,
    denomination: 'Produits finis',
    type: 'FAMILLE_ARTICLE',
    uniteRef: null,
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 3.09,
  },
  {
    id: ENUM_UNIT_KG,
    denomination: 'Kilogramme',
    type: 'UNITE',
    uniteRef: 'KG',
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 4,
  },
  {
    id: ENUM_UNIT_L,
    denomination: 'Litre',
    type: 'UNITE',
    uniteRef: 'L',
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 4,
  },
  {
    id: ENUM_UNIT_U,
    denomination: 'Unite',
    type: 'UNITE',
    uniteRef: 'UNITE',
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 4,
  },
  {
    id: ENUM_FAM_TECH,
    denomination: 'Biscuits',
    type: 'FAMILLE_TECHNIQUE',
    uniteRef: null,
    coefficient: 1,
    parentId: null,
    isActive: true,
    availableAt: 3.14,
  },
  {
    id: 'enu00007-aaaa-4bbb-cccc-dddddddddddd',
    denomination: 'Biscuits',
    type: 'SOUS_FAMILLE_ARTICLE',
    uniteRef: null,
    coefficient: 1,
    parentId: ENUM_FAM_PF,
    isActive: true,
    availableAt: 3.14,
  },
];

// ---------------------------------------------------------------------------
// ArticleInfos builder
// ---------------------------------------------------------------------------

function makeArticleInfos(overrides: Record<string, any>): any {
  return {
    id: overrides['id'],
    isActive: true,
    denomination: overrides['denomination'] ?? '',
    code: overrides['code'] ?? '',
    ean13: overrides['ean13'] ?? '',
    ean14: overrides['ean14'] ?? '',
    ean128: [],
    densite: overrides['densite'] ?? 0,
    bio: overrides['bio'] ?? false,
    versionSpecif: '',
    gross_weight_uc: overrides['gross_weight_uc'] ?? 0,
    net_weight_uc: overrides['net_weight_uc'] ?? 0,
    longueur_uc: 0, hauteur_uc: 0, largeur_uc: 0,
    gross_weight_colis: 0, net_weight_colis: 0,
    longueur_colis: 0, hauteur_colis: 0, largeur_colis: 0,
    ingredients: overrides['ingredients'] ?? '',
    allergenes: overrides['allergenes'] ?? '',
    photo: '',
    infosPrixAchat: overrides['infosPrixAchat'] ?? [],
    infosPrixVente: overrides['infosPrixVente'] ?? [],
    dluoJours: overrides['dluoJours'] ?? 0,
    dluoMois: overrides['dluoMois'] ?? 0,
    dluoAnnes: overrides['dluoAnnes'] ?? 0,
    dluoFormat: null, typePeriode: null, ddmAsRef: false,
    usedBy: null,
    famille: overrides['famille'] ?? null,
    familleId: overrides['familleId'] ?? null,
    categorie: null, categorieId: null,
    sousFamille: overrides['sousFamille'] ?? null, sousFamilleId: overrides['sousFamilleId'] ?? null,
    unite: overrides['unite'] ?? null,
    uniteId: overrides['uniteId'] ?? null,
    withLot: overrides['withLot'] ?? true,
    withTraca: overrides['withTraca'] ?? false,
    withPiedDeChaine: overrides['withPiedDeChaine'] ?? false,
    withConsoTraca: overrides['withConsoTraca'] ?? false,
    withStockPositif: overrides['withStockPositif'] ?? true,
    withEmplacement: false, withSpecif: overrides['withSpecif'] ?? false, withParallelOpe: false,
    withValeursNutritionnelles: false,
    isPrestation: false, isAlimentaire: true,
    proprietaire: null, proprietaireId: null,
    pcb: overrides['pcb'] ?? 1,
    user: null, groupId: null, codeClient: '',
    familleCode: '', sousFamilleCode: '',
    uniteCode: overrides['uniteCode'] ?? '',
    uniteDeComptageCode: '',
    secable: false, laize: 0, epaisseur: 0, microbio: '',
    infoFicheTechnique: { label: '', decription: '' },
    weight_suremballage: 0, tailleEchantillon: 0,
    typeComptage: null, refCalcul: null, quantiteParDimension: 0,
    valeurUnitaire: 0, uniteDeComptage: null, coefficientComptage: 1,
    nbUnitePerPack: 0, diametreMandrin: 0, diametreComplet: 0,
    specificationEtapeId: null, specificationEtapeMasterId: null,
    specificationEtapeMaster: null,
    valeurNutritionnellesLigne: [], coefficientVN: 0, labelBisVN: '', mlVN: false,
    typeFiche: null,
    articleId: overrides['articleId'] ?? null,
    planifDisplayMode: 'chart',
    stockReappro: 0, quantiteOfDefault: 0, stockSecurite: 0,
    inventaireId: null,
  };
}

// ---------------------------------------------------------------------------
// ArticleInfos instances
// ---------------------------------------------------------------------------

const familleMP = customEnums.find(e => e.id === ENUM_FAM_MP)!;
const famillePF = customEnums.find(e => e.id === ENUM_FAM_PF)!;
const uniteKG   = customEnums.find(e => e.id === ENUM_UNIT_KG)!;
const uniteL    = customEnums.find(e => e.id === ENUM_UNIT_L)!;
const uniteU    = customEnums.find(e => e.id === ENUM_UNIT_U)!;

// ---------------------------------------------------------------------------
// Profil prospect — personnalisation des libellés de démo
// ---------------------------------------------------------------------------
// Affiche la démo à l'effigie d'un prospect : on ne change QUE les libellés
// visibles (denominations). Les codes (MP-FAR-001…), ids et la structure
// relationnelle restent stables (ils pilotent des data-testid / matchings).
// Override : localStorage 'tutorial-prospect-profile' = JSON partiel, ex.
//   { "company":"Fromagerie du Val", "product":"Tomme de montagne",
//     "ingredients":["Lait cru","Présure","Sel","Ferments","Eau"],
//     "client":"Crémerie Léon", "supplier":"Ferme des Hauts" }
export interface ProspectProfile {
  company: string;
  product: string;
  ingredients: [string, string, string, string, string]; // farine, beurre, sucre, oeufs, lait
  client: string;
  supplier: string;
}
const DEFAULT_PROSPECT_PROFILE: ProspectProfile = {
  company: 'Moulin de Provence',
  product: 'Biscuits sablés du Moulin',
  ingredients: ['Farine de blé T65', 'Beurre doux 82%', 'Sucre cristal', 'Oeufs frais calibre M', 'Lait entier pasteurisé'],
  client: 'Boulangerie Martin',
  supplier: 'Minoterie Dupont',
};
function _loadProspectProfile(): ProspectProfile {
  try {
    const raw = (typeof localStorage !== 'undefined') ? localStorage.getItem('tutorial-prospect-profile') : null;
    if (!raw) return DEFAULT_PROSPECT_PROFILE;
    const partial = JSON.parse(raw);
    return {
      ...DEFAULT_PROSPECT_PROFILE,
      ...partial,
      ingredients: Array.isArray(partial.ingredients) && partial.ingredients.length === 5
        ? partial.ingredients : DEFAULT_PROSPECT_PROFILE.ingredients,
    };
  } catch { return DEFAULT_PROSPECT_PROFILE; }
}
export const PROSPECT_PROFILE: ProspectProfile = _loadProspectProfile();

// Farine : PDC + Traça (ingrédient principal, géré au pied de chaîne)
const articleInfosFarine = makeArticleInfos({
  id: AI_FARINE, denomination: PROSPECT_PROFILE.ingredients[0], code: 'MP-FAR-001',
  ean13: '3012345678901', bio: false,
  ingredients: 'Farine de ble (gluten)', allergenes: 'Gluten',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStockPositif: true, withPiedDeChaine: true, withTraca: true,
  net_weight_uc: 25, pcb: 1, articleId: ART_FARINE,
  infosPrixAchat: [{ quantiteMin: 0, prix: 0.85, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 1.20, isPrixAchat: false, articleFourniId: null }],
});

// Beurre : Traça seulement (pas de PDC)
const articleInfosBeurre = makeArticleInfos({
  id: AI_BEURRE, denomination: PROSPECT_PROFILE.ingredients[1], code: 'MP-BEU-002',
  ean13: '3012345678918', bio: false,
  ingredients: 'Creme pasteurisee (lait)', allergenes: 'Lait',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStockPositif: true, withTraca: true,
  net_weight_uc: 10, pcb: 1, articleId: ART_BEURRE,
  infosPrixAchat: [{ quantiteMin: 0, prix: 5.40, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 7.80, isPrixAchat: false, articleFourniId: null }],
});

// Sucre : Traça seulement (pas de PDC)
const articleInfosSucre = makeArticleInfos({
  id: AI_SUCRE, denomination: PROSPECT_PROFILE.ingredients[2], code: 'MP-SUC-003',
  ean13: '3012345678925', bio: false,
  ingredients: 'Sucre de betterave', allergenes: '',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStockPositif: true, withTraca: true,
  net_weight_uc: 25, pcb: 1, articleId: ART_SUCRE,
  infosPrixAchat: [{ quantiteMin: 0, prix: 0.72, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 1.05, isPrixAchat: false, articleFourniId: null }],
});

// Oeufs : Traça seulement (pas de PDC)
const articleInfosOeufs = makeArticleInfos({
  id: AI_OEUFS, denomination: PROSPECT_PROFILE.ingredients[3], code: 'MP-OEU-004',
  ean13: '3012345678932', bio: false,
  ingredients: 'Oeuf de poule', allergenes: 'Oeufs',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteU, uniteId: ENUM_UNIT_U, uniteCode: 'UNITE',
  withLot: true, withStockPositif: true, withTraca: true,
  net_weight_uc: 0.06, pcb: 30, articleId: ART_OEUFS,
  infosPrixAchat: [{ quantiteMin: 0, prix: 0.15, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 0.22, isPrixAchat: false, articleFourniId: null }],
});

// Lait : Déclaration des consommations (consoTraca, pas de PDC)
const articleInfosLait = makeArticleInfos({
  id: AI_LAIT, denomination: PROSPECT_PROFILE.ingredients[4], code: 'MP-LAI-005',
  ean13: '3012345678949', bio: false,
  ingredients: 'Lait entier de vache', allergenes: 'Lait',
  famille: familleMP, familleId: ENUM_FAM_MP,
  unite: uniteL, uniteId: ENUM_UNIT_L, uniteCode: 'L',
  withLot: true, withStockPositif: true, withTraca: true, withConsoTraca: true,
  net_weight_uc: 1, pcb: 6, articleId: ART_LAIT,
  infosPrixAchat: [{ quantiteMin: 0, prix: 0.95, isPrixAchat: true, articleFourniId: null }],
  infosPrixVente: [{ quantiteMin: 0, prix: 1.35, isPrixAchat: false, articleFourniId: null }],
});

const articleInfosBiscuits = makeArticleInfos({
  id: AI_BISCUITS, denomination: PROSPECT_PROFILE.product, code: 'PF-BIS-001',
  ean13: '3700799000006', bio: false,
  ingredients: 'Farine de ble (gluten), beurre (lait), sucre, oeufs, lait',
  allergenes: 'Gluten, Lait, Oeufs',
  famille: famillePF, familleId: ENUM_FAM_PF,
  sousFamille: { id: ENUM_SOUS_FAM_BISC, denomination: 'Biscuits', type: 'SOUS_FAMILLE_ARTICLE', uniteRef: null, coefficient: 1, parentId: ENUM_FAM_PF, isActive: true },
  sousFamilleId: ENUM_SOUS_FAM_BISC,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStockPositif: true, withSpecif: true,
  // gross_weight_uc en kg/KG vendu : permet le recalcul du poids brut palette
  // dans le tuto préparation-expédition (100 KG → ~105 kg + tare 25)
  net_weight_uc: 0.250, gross_weight_uc: 1.05, pcb: 12, articleId: ART_BISCUITS,
  infosPrixAchat: [],
  infosPrixVente: [{ quantiteMin: 0, prix: 12.50, isPrixAchat: false, articleFourniId: null }],
});

const articleInfosList = [
  articleInfosFarine, articleInfosBeurre, articleInfosSucre,
  articleInfosOeufs, articleInfosLait, articleInfosBiscuits,
];

// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------

function makeArticle(id: string, infos: any, semId?: string): any {
  return {
    id, isActive: true, version: 1,
    inventaireId: null, articleInfos: infos,
    stockReappro: 0, stockSecurite: 0, quantiteOfDefault: 0, qu: 0,
    specificationEtapeMasterId: semId ?? null, modelSpecificationEtapeMasterId: null,
    planifDisplayMode: 'chart',
    gross_weight_uc: 0, net_weight_uc: infos.net_weight_uc,
    longueur_uc: 0, hauteur_uc: 0, largeur_uc: 0,
    gross_weight_colis: 0, net_weight_colis: 0,
    longueur_colis: 0, hauteur_colis: 0, largeur_colis: 0,
    pcb: infos.pcb, weight_suremballage: 0,
    articleFournis: [], denominationCommerciale: '', tva: 5.5,
    nbChangementLots: 0, sumConsoChangementLots: 0,
    unite: infos.uniteCode, qttUnitaire: 0, qttTheoriqueTotale: 0,
  };
}

const articles = [
  makeArticle(ART_FARINE, articleInfosFarine, SEM_IDS[0]),
  makeArticle(ART_BEURRE, articleInfosBeurre, SEM_IDS[1]),
  makeArticle(ART_SUCRE,  articleInfosSucre,  SEM_IDS[2]),
  makeArticle(ART_OEUFS,  articleInfosOeufs,  SEM_IDS[3]),
  makeArticle(ART_LAIT,   articleInfosLait,   SEM_IDS[4]),
  makeArticle(ART_BISCUITS, articleInfosBiscuits, SEM_IDS[5]),
];

// Seuils de réappro/sécurité pour le tuto planification des composants :
// la farine (stock 500, consommée de 300 par l'OF) passe sous son seuil
// reappro (300) → article mis en évidence comme critique.
articles[0].stockReappro = 300; articles[0].stockSecurite = 150;
articles[1].stockReappro = 60;  articles[1].stockSecurite = 30;
articles[2].stockReappro = 200; articles[2].stockSecurite = 100;
// Le builder de planif lit les seuils sur l'ArticleInfos (pas sur l'Article) :
// on les pose donc aussi sur les ArticleInfos correspondants.
articleInfosFarine.stockReappro = 300; articleInfosFarine.stockSecurite = 150;
articleInfosBeurre.stockReappro = 60;  articleInfosBeurre.stockSecurite = 30;
articleInfosSucre.stockReappro = 200;  articleInfosSucre.stockSecurite = 100;

// ---------------------------------------------------------------------------
// Adresses
// ---------------------------------------------------------------------------

const adresses = [
  {
    id: ADR_ENTREPOT, denomination: 'Entrepôt principal',
    titre: null, titreCode: null, nom: '', prenom: '',
    telephone: '04 90 12 34 56', mail: 'entrepot@moulindeprovence.fr',
    infoOuverture: 'Lun-Ven 6h-20h', commentaire: '',
    numero: '12', numeroComplement: null, voie: 'Route des Alpilles',
    complement: 'Zone Artisanale', complement2: '',
    cp: '13520', commune: 'Maussane-les-Alpilles', pays: 'France',
    livraisonParDefaut: false, tiersId: null,
    isPiedDeChaine: false, isZoneReception: false, isZoneExpedition: false,
    isZoneNonConforme: false, isZoneQuarantaine: false, isZoneTransfert: false,
    adresseRefId: null, commandeId: null, ofId: null, isPerso: false, isActive: true,
  },
  {
    id: ADR_MOULIN, denomination: PROSPECT_PROFILE.company + ' - Siège',
    titre: null, titreCode: null, nom: 'Reynaud', prenom: 'Pierre',
    telephone: '04 90 12 34 56', mail: 'contact@moulindeprovence.fr',
    infoOuverture: 'Lun-Ven 7h-17h', commentaire: '',
    numero: '12', numeroComplement: null, voie: 'Route des Alpilles',
    complement: 'Zone Artisanale', complement2: '',
    cp: '13520', commune: 'Maussane-les-Alpilles', pays: 'France',
    livraisonParDefaut: true, tiersId: TIERS_MOULIN,
    isPiedDeChaine: false, isZoneReception: false, isZoneExpedition: false,
    isZoneNonConforme: false, isZoneQuarantaine: false, isZoneTransfert: false,
    adresseRefId: null, commandeId: null, ofId: null, isPerso: false, isActive: true,
  },
  {
    id: ADR_BOULANG, denomination: PROSPECT_PROFILE.client + ' - Boutique',
    titre: null, titreCode: null, nom: 'Martin', prenom: 'Sophie',
    telephone: '04 91 23 45 67', mail: 'commandes@boulangerie-martin.fr',
    infoOuverture: 'Mar-Sam 6h-19h', commentaire: '',
    numero: '8', numeroComplement: null, voie: 'Rue de la Republique',
    complement: '', complement2: '',
    cp: '13001', commune: 'Marseille', pays: 'France',
    livraisonParDefaut: true, tiersId: TIERS_BOULANG,
    isPiedDeChaine: false, isZoneReception: false, isZoneExpedition: false,
    isZoneNonConforme: false, isZoneQuarantaine: false, isZoneTransfert: false,
    adresseRefId: null, commandeId: null, ofId: null, isPerso: false, isActive: true,
  },
  {
    id: ADR_SUPER, denomination: 'Supermarche FraisMalin - Entrepot',
    titre: null, titreCode: null, nom: 'Dubois', prenom: 'Laurent',
    telephone: '04 42 56 78 90', mail: 'logistique@fraismalin.fr',
    infoOuverture: 'Lun-Sam 5h-21h', commentaire: 'Quai de dechargement n3',
    numero: '150', numeroComplement: null, voie: 'Avenue du Commerce',
    complement: 'ZAC Les Milles', complement2: '',
    cp: '13290', commune: 'Aix-en-Provence', pays: 'France',
    livraisonParDefaut: true, tiersId: TIERS_SUPER,
    isPiedDeChaine: false, isZoneReception: false, isZoneExpedition: false,
    isZoneNonConforme: false, isZoneQuarantaine: false, isZoneTransfert: false,
    adresseRefId: null, commandeId: null, ofId: null, isPerso: false, isActive: true,
  },
];

// ---------------------------------------------------------------------------
// Tiers
// ---------------------------------------------------------------------------

const tiers = [
  {
    id: TIERS_MOULIN, denomination: PROSPECT_PROFILE.company,
    docId: null, telephone: '04 90 12 34 56', mail: 'contact@moulindeprovence.fr',
    mailCommande: 'commandes@moulindeprovence.fr', mailCommandeClient: '',
    mailFacturation: 'compta@moulindeprovence.fr', mailLivraison: 'logistique@moulindeprovence.fr',
    siret: '412 345 678 00012', naf: '1061A', tva_intracom: 'FR12412345678',
    reduction: 0, francoQuantite: 500, francoPrix: 0, isClient: false, isFournisseur: true,
    adresses: [adresses[0]], isTransporteur: false,
    contact: [], primaryContact: null, articlesFournis: [],
    typeReglement: 'VIREMENT', delaiReglement: 'FDM', delaiReglementJour: 30,
    adresseLivraison: adresses[0], adresseLivraisonId: ADR_MOULIN,
    adresseFacturation: adresses[0], adresseFacturationId: ADR_MOULIN,
    ribPaiement: null, tierGroups: [], isActive: true,
  },
  {
    id: TIERS_BOULANG, denomination: PROSPECT_PROFILE.client,
    docId: null, telephone: '04 91 23 45 67', mail: 'contact@boulangerie-martin.fr',
    mailCommande: '', mailCommandeClient: 'commandes@boulangerie-martin.fr',
    mailFacturation: 'compta@boulangerie-martin.fr', mailLivraison: '',
    siret: '523 456 789 00034', naf: '1071C', tva_intracom: 'FR23523456789',
    reduction: 0, francoQuantite: 0, francoPrix: 0, isClient: true, isFournisseur: false,
    adresses: [adresses[1]], isTransporteur: false,
    contact: [], primaryContact: null, articlesFournis: [],
    typeReglement: 'VIREMENT', delaiReglement: 'FDM', delaiReglementJour: 30,
    adresseLivraison: adresses[1], adresseLivraisonId: ADR_BOULANG,
    adresseFacturation: adresses[1], adresseFacturationId: ADR_BOULANG,
    ribPaiement: null, tierGroups: [], isActive: true,
  },
  {
    id: TIERS_SUPER, denomination: 'Supermarche FraisMalin',
    docId: null, telephone: '04 42 56 78 90', mail: 'contact@fraismalin.fr',
    mailCommande: '', mailCommandeClient: 'achat@fraismalin.fr',
    mailFacturation: 'factures@fraismalin.fr', mailLivraison: 'logistique@fraismalin.fr',
    siret: '634 567 890 00056', naf: '4711F', tva_intracom: 'FR34634567890',
    reduction: 2, francoQuantite: 0, francoPrix: 300, isClient: true, isFournisseur: false,
    adresses: [adresses[2]], isTransporteur: false,
    contact: [], primaryContact: null, articlesFournis: [],
    typeReglement: 'PRELEVEMENT', delaiReglement: 'FDM', delaiReglementJour: 45,
    adresseLivraison: adresses[2], adresseLivraisonId: ADR_SUPER,
    adresseFacturation: adresses[2], adresseFacturationId: ADR_SUPER,
    ribPaiement: null, tierGroups: [], isActive: true,
  },
  {
    id: TIERS_TRANSP, denomination: 'Transports Rapides SARL',
    docId: null, telephone: '04 90 55 66 77', mail: 'contact@transports-rapides.fr',
    mailCommande: '', mailCommandeClient: '', mailFacturation: '', mailLivraison: '',
    siret: '745 678 901 00078', naf: '4941A', tva_intracom: 'FR45745678901',
    reduction: 0, francoQuantite: 0, francoPrix: 0, type: 'TRANSPORTEUR',
    adresses: [], isTransporteur: true,
    contact: [], primaryContact: null, articlesFournis: [],
    typeReglement: 'VIREMENT', delaiReglement: 'FDM', delaiReglementJour: 30,
    adresseLivraison: null, adresseLivraisonId: null,
    adresseFacturation: null, adresseFacturationId: null,
    ribPaiement: null, tierGroups: [], isActive: true,
  },
];

// ---------------------------------------------------------------------------
// Lots
// ---------------------------------------------------------------------------

const _now = new Date();
const _inSixMonths       = new Date(_now.getFullYear(), _now.getMonth() + 6, _now.getDate()).toISOString();
const _inTwelveMonths    = new Date(_now.getFullYear(), _now.getMonth() + 12, _now.getDate()).toISOString();
const _inTwentyFourMonths = new Date(_now.getFullYear(), _now.getMonth() + 24, _now.getDate()).toISOString();
const _inTwentyEightDays = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 28).toISOString();
const _inSevenDays       = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 7).toISOString();

function makeLot(
  id: string, articleId: string, article: any, infos: any,
  numero: string, dluo: string, quantite: number, cout: number,
): any {
  return {
    id, articleId, article, numero, dluo, dluoString: dluo,
    enQuarantaine: false, mouvements: [], receptionsCommandes: [],
    documentListeId: null, inventaireId: null, groupId: null,
    codeArticle: infos.code, quantite: String(quantite),
    dateMouvement: _now.toISOString(), dateCreation: _now,
    coutMaterielUnitaire: cout, coutPersonnelUnitaire: 0,
    lotUnique: false, quantiteDisplay: quantite,
    ofId: null, ofNumero: null, articleInfos: infos,
    // La vue « Stocks » de la fiche lot lit lot.stocks : sans entrée, elle
    // affiche un total 0 (tuto traçabilité).
    stocks: [
      { id: 'stk-' + id + '-ent', lotId: id, quantite: Number(quantite) * 0.8, adresseId: ADR_ENTREPOT, adresse: adresses[0], emplacementId: null, paletteId: null },
      { id: 'stk-' + id + '-sie', lotId: id, quantite: Number(quantite) * 0.2, adresseId: ADR_MOULIN, adresse: adresses[1], emplacementId: null, paletteId: null },
    ],
    isActive: true,
  };
}

const lots = [
  makeLot(LOT_FARINE, ART_FARINE, articles[0], articleInfosFarine, 'FAR-2026-001', _inTwelveMonths,    500, 0.85),
  makeLot(LOT_BEURRE, ART_BEURRE, articles[1], articleInfosBeurre, 'BEU-2026-001', _inSixMonths,       100, 5.40),
  makeLot(LOT_SUCRE,  ART_SUCRE,  articles[2], articleInfosSucre,  'SUC-2026-001', _inTwentyFourMonths, 300, 0.72),
  makeLot(LOT_OEUFS,  ART_OEUFS,  articles[3], articleInfosOeufs,  'OEU-2026-001', _inTwentyEightDays,  360, 0.15),
  makeLot(LOT_LAIT,   ART_LAIT,   articles[4], articleInfosLait,   'LAI-2026-001', _inSevenDays,        200, 0.95),
  makeLot(LOT_BISCUITS, ART_BISCUITS, articles[5], articleInfosBiscuits, 'BIS-2026-001', _inSixMonths, 250, 12.50),
];

// ---------------------------------------------------------------------------
// Commandes fournisseur
// ---------------------------------------------------------------------------

const _threeDaysAgo = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() - 3).toISOString();
const _fiveDaysAgo  = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() - 5).toISOString();
const _inTwoDays    = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 2).toISOString();
const _inFourDays   = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 4).toISOString();

function makeCfLigne(
  id: string, articleId: string, quantite: number, prix: number,
  uniteGestion: string, refFournisseur: string, commandeId: string, index: number,
): any {
  return {
    id, articleFourniId: null, articleId, quantite, quantiteAchat: quantite,
    prix, prixAvantRemise: prix, prixManuel: false, tva: 5.5,
    uniteGestion, coefficientUniteGestion: 1, refFournisseur,
    commandeId, receptions: [], commande: null, index, remiseLignes: [], isActive: true,
  };
}

const commandesFournisseur = [
  {
    id: CF_001, etat: 'EN_ATTENTE_LIVRAISON', numero: 'CF2600001',
    adresseLivraison: adresses[0], adresseLivraisonId: ADR_MOULIN,
    commentaire: 'Livraison matieres premieres semaine 20',
    provisoire: false, tiers: tiers[0], tiersId: TIERS_MOULIN,
    lignes: [
      makeCfLigne(CFL_001, ART_FARINE, 500, 0.85, 'KG', 'FAR-T65-25', CF_001, 0),
      makeCfLigne(CFL_002, ART_BEURRE, 100, 5.40, 'KG', 'BEU-82-10',  CF_001, 1),
    ],
    dateLivraisonPrevue: _inTwoDays, dateCreation: _fiveDaysAgo, dateValidation: _threeDaysAgo,
    dateFermeture: null, dateSoldee: null, dateAnnulation: null, dateManuelle: false,
    annulateur: null, annulateurId: null, createur: null, createurId: null,
    soldeur: null, soldeurId: null, validator: null, validatorId: null,
    documentListeId: null, isActive: true,
  },
  {
    id: CF_002, etat: 'EN_REDACTION', numero: 'CF2600002',
    adresseLivraison: adresses[0], adresseLivraisonId: ADR_MOULIN,
    commentaire: '', provisoire: false, tiers: tiers[0], tiersId: TIERS_MOULIN,
    lignes: [
      makeCfLigne(CFL_003, ART_SUCRE, 300, 0.72, 'KG', 'SUC-CRIST-25', CF_002, 0),
    ],
    dateLivraisonPrevue: _inFourDays, dateCreation: _now.toISOString(), dateValidation: null,
    dateFermeture: null, dateSoldee: null, dateAnnulation: null, dateManuelle: false,
    annulateur: null, annulateurId: null, createur: null, createurId: null,
    soldeur: null, soldeurId: null, validator: null, validatorId: null,
    documentListeId: null, isActive: true,
  },
  {
    id: CF_003, etat: 'EN_REDACTION', numero: 'CF2600003',
    adresseLivraison: adresses[0], adresseLivraisonId: ADR_MOULIN,
    commentaire: '', provisoire: false, tiers: tiers[0], tiersId: TIERS_MOULIN,
    lignes: [],
    dateLivraisonPrevue: _inFourDays, dateCreation: _now.toISOString(), dateValidation: null,
    dateFermeture: null, dateSoldee: null, dateAnnulation: null, dateManuelle: false,
    annulateur: null, annulateurId: null, createur: null, createurId: null,
    soldeur: null, soldeurId: null, validator: null, validatorId: null,
    documentListeId: null, isActive: true,
    // This commande is only visible after the tuto creates it (availableAt = section 3 progress)
    availableAt: 15.25,
  },
];

// ---------------------------------------------------------------------------
// Commandes client
// ---------------------------------------------------------------------------

function makeCcLigne(
  id: string, articleId: string, quantite: number, prix: number,
  uniteGestion: string, code: string, label: string,
  commandeClientId: string, index: number, refClient: string,
): any {
  return {
    id, articleFourniId: null, articleId, expeditions: [],
    quantite, quantiteArticle: quantite, quantiteLivree: 0, quantiteFacturee: 0,
    uniteGestion, prix, prixAvantRemise: prix, prixManuel: false, tva: 5.5,
    uuid: `ccl-uuid-${id.slice(-3)}`, codeArticle: code,
    commandeClient: null, commandeClientId, coefficientUniteGestion: 1,
    refClient, label, ofId: null, of: null, index,
    remiseLignes: [], discountType: null, discountValue: 0, isActive: true,
  };
}

const commandesClient = [
  {
    id: CC_001, numero: 'CC2600001', numeroCommandeClient: 'BM-2026-042',
    etat: 'EN_REDACTION', etatPaiement: null,
    etatExpedition: null, etatFacturation: null, facturesProvisoires: false,
    adresseLivraison: adresses[1], adresseLivraisonId: ADR_BOULANG,
    adresseFacturation: adresses[1], adresseFacturationId: ADR_BOULANG,
    entrepotDepart: null, entrepotDepartId: null, entrepots: [],
    tiers: tiers[1], tiersId: TIERS_BOULANG,
    lignes: [
      { ...makeCcLigne(CCL_001, ART_BISCUITS, 100, 12.50, 'KG', 'PF-BIS-001', 'Biscuits sables du Moulin', CC_001, 0, ''), availableAt: 17.16 },
    ],
    dateLivraisonPrevue: _inTwoDays, dateAnnulation: null,
    createur: null, createurId: null,
    dateValidation: _threeDaysAgo, validator: null, validatorId: null,
    dateCreation: _fiveDaysAgo,
    datePreparation: null, preparator: null, preparatorId: null,
    datePrete: null, pretator: null, pretatorId: null,
    dateExpedition: null, expeditor: null, expeditorId: null,
    dateTermine: null, terminator: null, terminatorId: null,
    documentListeId: null,
    remisePied: null, remisePiedType: null, remisePiedLabel: null,
    statusHistory: null, isActive: true,
  },
  {
    id: CC_002, numero: 'CC2600002', numeroCommandeClient: 'FM-PO-8834',
    etat: 'EN_ATTENTE_CONFIRMATION', etatPaiement: null,
    etatExpedition: null, etatFacturation: null, facturesProvisoires: false,
    adresseLivraison: adresses[2], adresseLivraisonId: ADR_SUPER,
    adresseFacturation: adresses[2], adresseFacturationId: ADR_SUPER,
    entrepotDepart: null, entrepotDepartId: null, entrepots: [],
    tiers: tiers[2], tiersId: TIERS_SUPER,
    lignes: [
      makeCcLigne(CCL_003, ART_BISCUITS, 250, 12.50, 'KG', 'PF-BIS-001', 'Biscuits sables du Moulin', CC_002, 0, 'REF-FM-BIS'),
    ],
    dateLivraisonPrevue: _inFourDays, dateAnnulation: null,
    createur: null, createurId: null,
    dateValidation: null, validator: null, validatorId: null,
    dateCreation: _now.toISOString(),
    datePreparation: null, preparator: null, preparatorId: null,
    datePrete: null, pretator: null, pretatorId: null,
    dateExpedition: null, expeditor: null, expeditorId: null,
    dateTermine: null, terminator: null, terminatorId: null,
    documentListeId: null,
    remisePied: null, remisePiedType: null, remisePiedLabel: null,
    statusHistory: null, isActive: true,
  },
];

// ---------------------------------------------------------------------------
// Exported demo data (also usable directly for targeted lookups)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// SpecificationEtapeMaster (wraps articles for the /specification_etape/master endpoint)
// ---------------------------------------------------------------------------

function makeSEM(semId: string, article: any, infos: any, seOverride?: string, etat = 'EN_REDACTION'): any {
  const seId = seOverride ?? (semId + '-se');
  return {
    id: semId,
    isActive: true,
    version: 1,
    denomination: infos.denomination,
    description: null,
    articleId: article.id,
    parentId: null,
    parent: null,
    childs: [],
    documentListeId: null,
    uniteGestion: infos.uniteCode,
    versions: [{
      id: seId,
      version_nb: 1,
      etat,
      infosArticleId: infos.id,
      infosArticle: infos,
      masterId: semId,
      nomenclatureId: null,
      nomenclature: null,
      process: [],
      podControlesProduction: null,
      podControlesReceptionsId: null,
      podControlesReceptions: null,
      podControlesDechargementId: null,
      podControlesDechargement: null,
      podControlesStartOpeId: null,
      podControlesStartOpe: null,
      podControlesEndOpeId: null,
      podControlesEndOpe: null,
      dateRedaction: new Date().toISOString(),
      dateValidation: new Date().toISOString(),
    }],
  };
}

const specificationMasters: any[] = [
  makeSEM(SEM_IDS[0], articles[0], articleInfosFarine),
  makeSEM(SEM_IDS[1], articles[1], articleInfosBeurre),
  makeSEM(SEM_IDS[2], articles[2], articleInfosSucre),
  makeSEM(SEM_IDS[3], articles[3], articleInfosOeufs),
  makeSEM(SEM_IDS[4], articles[4], articleInfosLait, undefined, 'EN_REDACTION'),
  makeSEM(SEM_IDS[5], articles[5], articleInfosBiscuits, SE_BISCUITS, 'EN_REDACTION'),
];

// Pre-populate podIds on Lait (reception) and Biscuits (start ope) so tutorial doesn't need createPod
specificationMasters[4].versions[0].podControlesReceptionsId = POD_RECEP_LAIT;
specificationMasters[4].versions[0].podControlesReceptions = { id: POD_RECEP_LAIT, controles: [] };
specificationMasters[5].versions[0].podControlesStartOpeId = POD_STARTOPE_BISC;
specificationMasters[5].versions[0].podControlesStartOpe = { id: POD_STARTOPE_BISC, controles: [] };

// ---------------------------------------------------------------------------
// Modèle de spécification paramétrable + variables (tuto modeles-specifications)
// ---------------------------------------------------------------------------
// Un MODÈLE est un SEM sans articleId. Il porte des variables (les "trous" à
// remplir par article). On le câble ensuite sur l'article Biscuits (SEM[5]).
const SEM_MODEL = 'a0a0a0a0-0001-4000-a000-000000000099';
const SE_MODEL  = 'se000099-aaaa-4bbb-cccc-dddddddddddd';

// variableUUID = identifiant STABLE qui relie la variable du modèle à sa copie
// sur l'article (le `id` change à chaque version, pas le variableUUID).
const VUUID_SUCRE  = 'fvar0001-aaaa-4bbb-cccc-dddddddddddd';
const VUUID_PARFUM = 'fvar0002-aaaa-4bbb-cccc-dddddddddddd';

function makeSpecVariable(o: Record<string, any>): any {
  return {
    id: o['id'],
    name: o['name'],
    parsedName: o['name'],
    description: o['description'] ?? '',
    type: o['type'],
    specificationEtapeId: o['specificationEtapeId'],
    parentVariableId: o['parentVariableId'] ?? null,
    variableUUID: o['variableUUID'],
    payload: o['payload'] ?? null,
    documentId: null,
    nomenclatureLigneId: null,
    nomenclatureLigneArticleId: null,
    processEtapeId: null,
    dontDo: false,
    isActive: true,
    version: 1,
  };
}

// Variables MAÎTRES du modèle (parentVariableId = null), portées par SE_MODEL.
const MODEL_VARIABLES: any[] = [
  makeSpecVariable({ id: 'fvar00m1-aaaa-4bbb-cccc-dddddddddddd', name: 'Taux de sucre cible (%)', type: 'NUMBER', specificationEtapeId: SE_MODEL, variableUUID: VUUID_SUCRE, payload: { value: 20 }, description: 'Pourcentage de sucre visé pour cette recette.' }),
  makeSpecVariable({ id: 'fvar00m2-aaaa-4bbb-cccc-dddddddddddd', name: 'Parfum dominant',        type: 'STRING', specificationEtapeId: SE_MODEL, variableUUID: VUUID_PARFUM, payload: { value: 'Vanille' }, description: 'Parfum principal du biscuit.' }),
];

// Le SEM modèle (articleId = null → c'est un modèle, pas une fiche article).
const specificationModelSEM: any = {
  id: SEM_MODEL,
  isActive: true,
  version: 1,
  denomination: 'Modèle — Biscuit sablé paramétrable',
  description: 'Spécification partagée : recette commune, variables ajustables par article.',
  articleId: null,
  parentId: null,
  parent: null,
  childs: [],
  documentListeId: null,
  uniteGestion: articleInfosBiscuits.uniteCode,
  versions: [{
    id: SE_MODEL,
    version_nb: 1,
    etat: 'EN_REDACTION',
    infosArticleId: null,
    infosArticle: null,
    masterId: SEM_MODEL,
    nomenclatureId: null,
    nomenclature: null,
    process: [],
    quantityProdDefault: 250,
    dateRedaction: new Date().toISOString(),
    dateValidation: new Date().toISOString(),
  }],
};
specificationMasters.push(specificationModelSEM);

// Biscuits (article) est associé à ce modèle → l'onglet "Spécification de
// l'article" montre l'association + l'héritage des variables.
specificationMasters[5].versions[0].modelSpecificationEtapeMasterId = SEM_MODEL;

// Variables vues côté ARTICLE Biscuits : sa copie (renseignée) du taux de sucre
// + les variables du modèle (pour calculer les "non renseignées" — ici Parfum).
const ARTICLE_VARIABLES_BISCUITS: any[] = [
  makeSpecVariable({ id: 'fvar00a1-aaaa-4bbb-cccc-dddddddddddd', name: 'Taux de sucre cible (%)', type: 'NUMBER', specificationEtapeId: SE_BISCUITS, parentVariableId: 'fvar00m1-aaaa-4bbb-cccc-dddddddddddd', variableUUID: VUUID_SUCRE, payload: { value: 22 } }),
  ...MODEL_VARIABLES,
];

// Modèles proposés dans le sélecteur d'association (etape-chooser).
const SPECIFICATION_MODELS_FOR_SELECTION = [specificationModelSEM];

// enrichBiscuitsSEM() is called after nomenclature/process/controles are defined (see below)

// ---------------------------------------------------------------------------
// Nomenclature (recipe for Biscuits sables du Moulin)
// ---------------------------------------------------------------------------

function makeNomenclatureLigne(
  id: string, articleId: string, articleCode: string, quantite: number,
  perteNormale: number, perteMax: number, index: number, infos: any,
): any {
  return {
    id, articleId, articleCode, quantite,
    perteNormale, perteMax,
    tiers: null, tiersId: null,
    specificationEtapeRefId: SE_BISCUITS,
    optional: false, articleDynamic: false, paramDynamic: false,
    description: '', parsedDescription: '', index,
    bricks: [], dontDo: false, lotHeritier: index === 0,
    isActive: true, version: 1,
    articleInfos: infos,
  };
}

// Quantités réalistes pour 250 kg de biscuits sablés (quantityProdDefault = 250)
// consoTheoriqueUnitaire = qty / 250, consoTheorique = consoTheoriqueUnitaire * 250
const nomenclatureLignes = [
  makeNomenclatureLigne(NOM_L_FARINE, ART_FARINE, 'MP-FAR-001', 100,   2, 5, 0, articleInfosFarine),  // 100 kg farine (40%)
  makeNomenclatureLigne(NOM_L_BEURRE, ART_BEURRE, 'MP-BEU-002',  62.5, 1, 3, 1, articleInfosBeurre),  //  62.5 kg beurre (25%)
  makeNomenclatureLigne(NOM_L_SUCRE,  ART_SUCRE,  'MP-SUC-003',  50,   1, 2, 2, articleInfosSucre),   //  50 kg sucre (20%)
  makeNomenclatureLigne(NOM_L_OEUFS,  ART_OEUFS,  'MP-OEU-004',  25,   2, 5, 3, articleInfosOeufs),   //  25 kg oeufs (10%)
  makeNomenclatureLigne(NOM_L_LAIT,   ART_LAIT,   'MP-LAI-005',  12.5, 1, 3, 4, articleInfosLait),    //  12.5 L lait (5%)
];

const nomenclatures = [
  {
    id: NOM_BISCUITS,
    isActive: true,
    version: 1,
    nomenclatureLignes,
    articleCode: 'PF-BIS-001',
    article: articles[5],
    specificationEtapeId: SE_BISCUITS,
    specificationEtape: null,
  },
];

// ---------------------------------------------------------------------------
// Process Etapes (fabrication steps for Biscuits)
// ---------------------------------------------------------------------------

function makeProcessEtape(
  id: string, index: number, label: string, description: string,
  timer: string, articleId: string,
  podId?: string,
): any {
  return {
    id, index, uuid: id, label, description,
    articleId,
    podId: podId ?? null, pod: null,
    docId: null, documentListeId: null,
    timer,
    parent: null, lignes: [],
    optional: false,
    specificationEtapeId: SE_BISCUITS,
    specificationEtapeRefId: SE_BISCUITS,
    done: false,
    promptAI: null,
    dontDo: false,
    controles: [],
    isActive: true, version: 1,
  };
}

const processEtapes = [
  makeProcessEtape(PE_MELANGE,   1, 'Melange sec',           'Melanger la farine et le sucre dans le petrin jusqu\'a obtenir un melange homogene.',           '5',  ART_BISCUITS, POD_MELANGE),
  makeProcessEtape(PE_INCORP_B,  2, 'Incorporation beurre',  'Ajouter le beurre froid coupe en des. Sabler la pate jusqu\'a obtenir une texture sableuse.',   '10', ART_BISCUITS),
  makeProcessEtape(PE_AJOUT_OL,  3, 'Ajout oeufs et lait',   'Incorporer les oeufs battus et le lait. Melanger sans trop travailler la pate.',                '5',  ART_BISCUITS),
  makeProcessEtape(PE_REPOS,     4, 'Repos de la pate',       'Filmer la pate et la laisser reposer au refrigerateur.',                                        '30', ART_BISCUITS),
  makeProcessEtape(PE_FACONNAGE, 5, 'Faconnage',              'Etaler la pate a 5mm d\'epaisseur et decouper les biscuits a l\'emporte-piece.',                 '15', ART_BISCUITS),
  makeProcessEtape(PE_CUISSON,   6, 'Cuisson',                'Enfourner a 175 C. Surveiller la coloration : les biscuits doivent etre dores mais pas bruns.',  '20', ART_BISCUITS, POD_CUISSON),
  makeProcessEtape(PE_REFROID,   7, 'Refroidissement',        'Laisser refroidir les biscuits sur grille a temperature ambiante.',                              '30', ART_BISCUITS),
  makeProcessEtape(PE_CONDITION, 8, 'Conditionnement',        'Peser et conditionner les biscuits en sachets de 250g. Etiqueter et dater.',                     '10', ART_BISCUITS, POD_CONDITION),
];

// ---------------------------------------------------------------------------
// Controles qualite
// ---------------------------------------------------------------------------

const controlePod: any = {
  id: CTRL_POD,
  isActive: true,
  version: 1,
  controles: [],  // populated below
};

function makeControle(overrides: Record<string, any>): any {
  return {
    id: overrides['id'],
    userId: null,
    index: overrides['index'] ?? 0,
    date: new Date(),
    type: overrides['type'],
    etat: 'EN_COURS',
    description: overrides['description'] ?? '',
    podId: CTRL_POD,
    docId: null,
    documentListeId: null,
    consigneNC: overrides['consigneNC'] ?? '',
    identifiantObligatoire: false,
    denomination: overrides['denomination'],
    events: '',
    controleur: null, controleurId: null,
    posteEvenement: null, posteEvenementId: null,
    tailleEchantillon: overrides['tailleEchantillon'] ?? 1,
    tailleEchantillonString: String(overrides['tailleEchantillon'] ?? 1),
    marquage: null,
    choix: overrides['choix'] ?? '',
    correctAnswer: overrides['correctAnswer'] ?? '',
    isPhoto: false, isValeur: false,
    unite: overrides['unite'] ?? '',
    avecCourbe: false,
    limiteHaute: overrides['limiteHaute'] ?? 0,
    limiteBasse: overrides['limiteBasse'] ?? 0,
    nominal: overrides['nominal'] ?? 0,
    limiteHauteString: String(overrides['limiteHaute'] ?? 0),
    limiteBasseString: String(overrides['limiteBasse'] ?? 0),
    nominalString: String(overrides['nominal'] ?? 0),
    typeNominal: overrides['typeNominal'] ?? null,
    moyenne: 0, valeurDynamique: false,
    isEcart: false, valeur1: '', valeur2: '',
    isJustupTodo: false, ccp: overrides['ccp'] ?? false, prpo: false,
    elementType: overrides['elementType'] ?? 'PRODUCTION',
    elementId: null, elementControlledId: null,
    parentControl: null, checklistId: null,
    model: true,
    enregistrements: [],
    uuid: overrides['id'],
    isValid: false,
    ecartMinutesString: '', toleranceMinutesString: '',
    processEtape: null, processEtapeId: overrides['processEtapeId'] ?? null,
    processEtapeRef: null, processEtapeRefId: null,
    specificationEtapeId: SE_BISCUITS, specificationEtapeRefId: SE_BISCUITS,
    articleId: ART_BISCUITS, articleVariableUUID: null,
    lignes: [], event: '',
    echantillonParQuantite: 0, controleParQuantite: false,
    articleInfosRefId: null,
    familles: [], categories: [], tiers: [], clients: [],
    expeditionUnitTypes: [], entrepots: [],
    valid: false,
    isActive: true, version: 1,
  };
}

// Contrôle 1 : CNC one-shot sur Mélange sec (pré-existant, visible dès section 3)
const ctrlAspectMelange: any = makeControle({
  id: CTRL_ASPECT, index: 1,
  denomination: 'Aspect du melange',
  description: 'Verifier que le melange farine-sucre est homogene, sans grumeaux visibles.',
  type: 'CNC',
  podId: POD_MELANGE,
  processEtapeId: PE_MELANGE,
  elementType: 'PROCESS',
  consigneNC: 'Prolonger le melange 2 minutes supplementaires et reverifier.',
});
ctrlAspectMelange.availableAt = 7.12; // visible when section 3 starts

// Contrôle 2 : Quantitatif récurrent sur Cuisson (toutes les 15 min, CCP)
const ctrlTempCuisson: any = makeControle({
  id: CTRL_TEMP, index: 1,
  denomination: 'Temperature du four',
  description: 'Mesurer la temperature du four avec le thermometre etalone.',
  type: 'QUANTITATIF',
  unite: '°C',
  limiteBasse: 170, limiteHaute: 180, nominal: 175,
  typeNominal: 'BETWEEN',
  ccp: true,
  podId: POD_CUISSON,
  processEtapeId: PE_CUISSON,
  elementType: 'PROCESS',
  consigneNC: 'Ajuster la temperature du four et attendre la stabilisation avant d\'enfourner.',
});
ctrlTempCuisson.ecartMinutesString = '15';
ctrlTempCuisson.toleranceMinutesString = '5';
ctrlTempCuisson.availableAt = 7.12;
ctrlTempCuisson.toleranceMinutesString = '5';

// Contrôle 3 : CNC sur événement sur Conditionnement
const ctrlEtiquette: any = makeControle({
  id: CTRL_ETIQ, index: 1,
  denomination: 'Changement rouleau etiquettes',
  description: 'Verifier la conformite de l\'etiquetage apres chaque changement de rouleau : lisibilite, DLC, code-barres.',
  type: 'CNC',
  podId: POD_CONDITION,
  processEtapeId: PE_CONDITION,
  elementType: 'PROCESS',
  consigneNC: 'Retirer les sachets etiquetes depuis le dernier controle et re-etiqueter.',
});
ctrlEtiquette.events = 'Changement rouleau etiquettes';
ctrlEtiquette.availableAt = 7.12;

// Contrôle 4 : Poids (gardé pour la production)
const ctrlPoids: any = makeControle({
  id: CTRL_POIDS, index: 2,
  denomination: 'Poids sachet',
  description: 'Peser un sachet de biscuits pour verifier le poids net.',
  type: 'QUANTITATIF',
  unite: 'g',
  limiteBasse: 245, limiteHaute: 255, nominal: 250,
  typeNominal: 'BETWEEN',
  tailleEchantillon: 3,
  podId: POD_CONDITION,
  processEtapeId: PE_CONDITION,
  elementType: 'PROCESS',
  consigneNC: 'Verifier le reglage de la peseuse. Ecarter les sachets hors tolerance.',
});
ctrlPoids.availableAt = 7.12;

// Contrôle 5 : CNC one-shot sur Mélange sec (créé par l'utilisateur pendant le tuto)
const ctrlHomogPate: any = makeControle({
  id: CTRL_HOMOG, index: 2,
  denomination: 'Homogeneite pate',
  description: 'Verifier texture uniforme, sans grumeaux.',
  type: 'CNC',
  podId: POD_MELANGE,
  processEtapeId: PE_MELANGE,
  elementType: 'PROCESS',
  consigneNC: 'Prolonger le petrissage et reverifier.',
});
ctrlHomogPate.availableAt = 7.27; // visible after proc-save-controle step

const controles = [ctrlAspectMelange, ctrlTempCuisson, ctrlEtiquette, ctrlPoids, ctrlHomogPate];

// Build pods with their controles
const podMelange: any = { id: POD_MELANGE, isActive: true, version: 1, controles: [ctrlAspectMelange, ctrlHomogPate] };
const podCuisson: any = { id: POD_CUISSON, isActive: true, version: 1, controles: [ctrlTempCuisson] };
const podCondition: any = { id: POD_CONDITION, isActive: true, version: 1, controles: [ctrlEtiquette, ctrlPoids] };
const controlePods = [podMelange, podCuisson, podCondition];

controlePod.controles = controles;

// Enrich processEtapes with their controles from pods
processEtapes.forEach((pe: any) => {
  const pod = controlePods.find((p: any) => p.id === pe.podId);
  if (pod) {
    pe.controles = pod.controles;
    pe.pod = pod;
  }
});

// Enrich Biscuits SEM with nomenclature + process + controles now that they're defined
(() => {
  const sem = specificationMasters[5];
  const se = sem.versions[0];
  se.nomenclatureId = NOM_BISCUITS;
  se.nomenclature = nomenclatures[0];
  se.process = processEtapes;
  se.podControlesProduction = controlePod;
})();

// ---------------------------------------------------------------------------
// Ligne de production
// ---------------------------------------------------------------------------

const ligneParam: any = {
  id: LIGNE_PARAM,
  ligneId: LIGNE_BISC,
  familleTechniqueId: ENUM_SOUS_FAM_BISC,
  familleTechnique: { id: ENUM_SOUS_FAM_BISC, denomination: 'Biscuits', type: 'SOUS_FAMILLE_ARTICLE', parentId: ENUM_FAM_PF, isActive: true },
  cadence: 50,
  nbPersonnes: 3,
  tempsCalage: 15,
  tempsNettoyage: 20,
  dureePoste: 480,
  isActive: true,
  version: 1,
};

const lignes: any[] = [
  {
    id: LIGNE_BISC,
    denomination: 'Ligne Biscuits',
    adresse: adresses[0],
    adresseId: ADR_MOULIN,
    docId: null,
    groupId: null,
    nbOF: 1,
    params: [ligneParam],
    firstParam: [],
    horaires: [{
      id: 'hor-ligne-bisc',
      ligneId: LIGNE_BISC,
      lastChangedDateTime: new Date('2026-01-01'),
      lundi: ['09:00-17:00'],
      mardi: ['09:00-17:00'],
      mercredi: ['09:00-17:00'],
      jeudi: ['09:00-17:00'],
      vendredi: ['09:00-17:00'],
      samedi: [],
      dimanche: [],
    }],
    lists: [],
    entityLinks: [],
    user: null,
    isActive: true, version: 1,
  },
];

// ---------------------------------------------------------------------------
// Ordre de Fabrication
// ---------------------------------------------------------------------------

const _dayOffset = (days: number) => new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + days).toISOString();

/** Renvoie le Nième jour ouvré à partir d'aujourd'hui (0 = aujourd'hui si ouvré, sinon lundi). */
function _workDay(n: number, hours = 0, minutes = 0): string {
  const d = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
  // Avance au premier jour ouvré si aujourd'hui est weekend
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  let count = 0;
  while (count < n) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) count++;
  }
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
}

function makeOf(id: string, numero: string, etat: string, quantite: number, quantiteProduite: number, startWorkDay: number, startH: number, endWorkDay: number | null, endH: number | null, started: boolean, available: number, extra: Record<string, any> = {}): any {
  const debut = _workDay(startWorkDay, startH);
  const fin = (endWorkDay != null && endH != null) ? _workDay(endWorkDay, endH) : null;
  return {
    id, numero, etat, pdcStatus: null,
    // Créés « hier » : la liste OF trie par createDateTime desc — l'OF que le
    // tuto crée (createDateTime = maintenant, posé par l'interceptor) doit
    // passer devant pour que « votre OF apparaît » spotlighte la 1re carte.
    createDateTime: _dayOffset(-1), lastChangedDateTime: _dayOffset(-1),
    articleId: ART_BISCUITS, article: articles[5],
    articleInfos: articleInfosBiscuits, articleInfosId: AI_BISCUITS,
    quantite, quantiteProduite,
    integrations: [], started,
    documentListeId: null, statsDocumentListeId: null,
    valideProd: false, valideQualite: false,
    dateReinteTemp: null,
    dateDebut: debut,
    dateFin: fin,
    dateDebutPrevue: debut,
    commandeClientLignes: [],
    lots: [], postes: [], mouvements: [], bilans: [],
    bilanProductivite: null, qttReinteTemp: 0,
    scoreTotal: 0, scoreMatiere: 0, scorePersonnel: 0,
    scoreMatiereSurcout: 0, scorePersonnelSurcout: 0,
    coutMatiereTotalTheorique: 0, coutMatiereTotalReel: 0,
    coutPersonnelTotalTheorique: 0, coutPersonnelTotalReel: 0,
    coutNcMatiere: 0, coutNcPersonnel: 0, coutNcRecuperable: 0,
    paramLigne: ligneParam, paramLigneId: LIGNE_PARAM,
    ligne: lignes[0], ligneId: LIGNE_BISC,
    specificationId: null, bilanMatiereIntermediaire: false,
    withQuantiteOpe: false, ops: '', pathId: null,
    posteId: null, doc: null,
    isActive: true, version: 1,
    availableAt: available,
    ...extra,
  };
}

// Horaires ligne : 09:00-17:00 (8h) lundi-vendredi. Cadence ~500u/8h.
// Les jours sont en JOURS OUVRÉS (0=aujourd'hui, 1=prochain jour ouvré, etc.)
//
// Progression availableAt (order=12, globalStepIndex/100) :
//   12.00 → OF1 visible au début du tuto planif (le Gantt le présente)
//   12.00 → OF2 visible au début du tuto planif (pour le dialog +)
//   12.08 → OF3-10 cascade après section 2 (reloadAfter)
//
// Flow du tuto :
//   - Section 1 : juste OF1 visible sur le Gantt (planifié depuis le form OF)
//   - Section 2 : on présente le +, on explique le mécanisme, cascade
//   - Section 3 : vue 7j avec tous les OF, drag & drop, fonctionnalités
//                                                                         qty   qp  wd  hD  wd    hF   started avail
const ofs: any[] = [
  makeOf(OF_BISCUITS,                              'OF2600001', 'EN_ATTENTE_PROD',    250,  0,   0,  9,  0, 13,   false, 12.00),
  makeOf('of000002-aaaa-4bbb-cccc-dddddddddddd',  'OF2600002', 'EN_ATTENTE_PLANNIF', 200,  0,   0, 14,  null, null, false, 12.00),
  makeOf('of000003-aaaa-4bbb-cccc-dddddddddddd',  'OF2600003', 'EN_ATTENTE_PROD',    280,  0,   1,  9,  1, 13,   false, 12.08),
  makeOf('of000004-aaaa-4bbb-cccc-dddddddddddd',  'OF2600004', 'EN_ATTENTE_PROD',    230,  0,   2,  9,  2, 13,   false, 12.08),
  makeOf('of000005-aaaa-4bbb-cccc-dddddddddddd',  'OF2600005', 'EN_ATTENTE_PLANNIF', 260,  0,   3,  9,  null, null, false, 12.08),
  makeOf('of000006-aaaa-4bbb-cccc-dddddddddddd',  'OF2600006', 'EN_ATTENTE_PROD',    300,  0,   3,  9,  3, 14,   false, 12.08),
  makeOf('of000007-aaaa-4bbb-cccc-dddddddddddd',  'OF2600007', 'EN_ATTENTE_PLANNIF', 220,  0,   4,  9,  null, null, false, 12.08),
  makeOf('of000008-aaaa-4bbb-cccc-dddddddddddd',  'OF2600008', 'EN_ATTENTE_PROD',    270,  0,   4,  9,  4, 13,   false, 12.08),
  makeOf('of000009-aaaa-4bbb-cccc-dddddddddddd',  'OF2600009', 'EN_ATTENTE_PLANNIF', 240,  0,   5,  9,  null, null, false, 12.08),
  makeOf('of000010-aaaa-4bbb-cccc-dddddddddddd',  'OF2600010', 'EN_ATTENTE_PROD',    290,  0,   5,  9,  5, 14,   false, 12.08),
];

// ---------------------------------------------------------------------------
// OF « d'ambiance » pour le tuto gestion-of : la
// liste ne doit pas être vide pendant qu'on la présente — on montre des OF
// d'articles variés à différents états (terminé / en cours / à planifier).
// Articles display-only : hors du tableau `articles`, ils n'apparaissent
// nulle part ailleurs (catalogue, sélecteurs…).
// availableUntil : visibles pendant TOUT le tuto gestion-of (l’OF créé passe
// devant via createDateTime), masqués pour les tutos suivants (le Gantt du tuto
// planification attend « juste OF1 »).
// ---------------------------------------------------------------------------
const AI_MADELEINES = 'e1f2a3b4-c5d6-4e7f-8a9b-000000000002';
const ART_MADELEINES = 'f6a7b8c9-d0e1-4f2a-3b4c-000000000002';
const AI_COOKIES = 'e1f2a3b4-c5d6-4e7f-8a9b-000000000003';
const ART_COOKIES = 'f6a7b8c9-d0e1-4f2a-3b4c-000000000003';
const articleInfosMadeleines = makeArticleInfos({
  id: AI_MADELEINES, denomination: 'Madeleines nature', code: 'PF-MAD-001',
  famille: famillePF, familleId: ENUM_FAM_PF,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStockPositif: true, articleId: ART_MADELEINES,
});
const articleMadeleines = makeArticle(ART_MADELEINES, articleInfosMadeleines);
const articleInfosCookies = makeArticleInfos({
  id: AI_COOKIES, denomination: 'Cookies pépites de chocolat', code: 'PF-COO-001',
  famille: famillePF, familleId: ENUM_FAM_PF,
  unite: uniteKG, uniteId: ENUM_UNIT_KG, uniteCode: 'KG',
  withLot: true, withStockPositif: true, articleId: ART_COOKIES,
});
const articleCookies = makeArticle(ART_COOKIES, articleInfosCookies);
ofs.push(
  // Terminé ce matin (validé) — 400/400 madeleines
  makeOf('of000090-aaaa-4bbb-cccc-dddddddddddd', 'OF2500961', 'VALIDE', 400, 400, 0, 6, 0, 9, true, 11, {
    articleId: ART_MADELEINES, article: articleMadeleines,
    articleInfos: articleInfosMadeleines, articleInfosId: AI_MADELEINES,
    valideProd: true, valideQualite: true, availableUntil: 12.00,
    lots: [{ numero: 'MAD-260706-01' }],
  }),
  // En cours de production — 180/350 cookies
  makeOf('of000091-aaaa-4bbb-cccc-dddddddddddd', 'OF2500962', 'EN_PROD', 350, 180, 0, 9, 0, 13, true, 11, {
    articleId: ART_COOKIES, article: articleCookies,
    articleInfos: articleInfosCookies, articleInfosId: AI_COOKIES,
    availableUntil: 12.00,
    lots: [{ numero: 'COO-260706-01' }],
  }),
  // À planifier cet après-midi — biscuits
  makeOf('of000092-aaaa-4bbb-cccc-dddddddddddd', 'OF2500963', 'EN_ATTENTE_PLANNIF', 250, 0, 0, 14, null, null, false, 11, {
    availableUntil: 12.00,
  }),
);

// ---------------------------------------------------------------------------
// Entreprise fictive
// ---------------------------------------------------------------------------

const activityInfos: any = {
  id: 'ai000001-aaaa-4bbb-cccc-dddddddddddd',
  denomination: 'Boulangerie du Moulin',
  fj: 'SARL',
  siret: '123 456 789 00012',
  capital: '50000',
  rcsVille: 'Bordeaux',
  rcsNumero: '123 456 789',
  codeAPE: '1071C',
  tvaIntracom: 'FR12345678901',
  email: 'contact@boulangerie-du-moulin.fr',
  emailFacture: 'compta@boulangerie-du-moulin.fr',
  telephone: '05 56 00 00 00',
  groupId: null,
  codeGS1: '370079900',
  textColor: false,
  color: '#2E7D32',
  devise: 'EUR',
  docId: null,
  infoCommandeFournisseur: '',
  infoFactureExpedition: '',
  infoAvoirClient: '',
  infoConfirmationCommandeClient: '',
  localIps: '',
  authorizedIps: '',
  horaires: [],
  offre: 'JUSTUP',
  validationSpecif: 'NONE',
  validationOF: 'SIMPLE',
  consigneNC: '',
  promptAIProcess: '',
  dateDebutExercice: '01/01',
  signatureOperateurObligatoire: false,
  isActive: true,
  version: 1,
};

// ---------------------------------------------------------------------------
// Helpers for traca module (progress >= 14) — pre-built poste and PEOs
// ---------------------------------------------------------------------------

const _PEO_UUIDS = [
  'b0b0b0b0-0001-4000-a000-000000000001',
  'b0b0b0b0-0002-4000-a000-000000000002',
  'b0b0b0b0-0003-4000-a000-000000000003',
  'b0b0b0b0-0004-4000-a000-000000000004',
  'b0b0b0b0-0005-4000-a000-000000000005',
  'b0b0b0b0-0006-4000-a000-000000000006',
  'b0b0b0b0-0007-4000-a000-000000000007',
  'b0b0b0b0-0008-4000-a000-000000000008',
];
const _POD_UUIDS = [
  'c0c0c0c0-0001-4000-a000-000000000001',
  'c0c0c0c0-0002-4000-a000-000000000002',
  'c0c0c0c0-0003-4000-a000-000000000003',
  'c0c0c0c0-0004-4000-a000-000000000004',
  'c0c0c0c0-0005-4000-a000-000000000005',
  'c0c0c0c0-0006-4000-a000-000000000006',
  'c0c0c0c0-0007-4000-a000-000000000007',
  'c0c0c0c0-0008-4000-a000-000000000008',
];

function _buildTutoPeos(): any[] {
  const now = new Date().toISOString();
  return processEtapes.map((pe: any, i: number) => {
    const hasControles = pe.controles?.length > 0;
    const podId = hasControles ? _POD_UUIDS[i] : undefined;
    return {
      id: _PEO_UUIDS[i],
      dateDebut: now, dateFin: now,
      poste: { id: 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4' }, posteId: 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4',
      processEtape: pe, processEtapeId: pe.id,
      podId: podId ?? null,
      pod: podId ? {
        id: podId, isActive: true, version: 1,
        controles: (pe.controles ?? []).map((c: any) => {
          const inst = { ...c };
          // Override spec properties to mark as realized instance
          inst.id = crypto.randomUUID();
          inst.podId = podId;
          inst.parentControl = c.id;
          inst.model = false;
          inst.etat = 'CONFORME';
          inst.createDateTime = now;
          inst.date = now;
          if (c.type === 'QUANTITATIF') {
            inst.valeur1 = String(c.nominal ?? 175);
            inst.valeur2 = String(c.nominal ?? 175);
            inst.moyenne = c.nominal ?? 175;
          }
          if (c.type === 'CNC') {
            inst.choix = c.correctAnswer || 'C';
          }
          inst.enregistrements = [{ saisie: c.type === 'QUANTITATIF' ? String(c.nominal ?? 175) : 'Conforme', valid: true, createDateTime: now }];
          return inst;
        }),
      } : null,
      state: 'DONE', optional: pe.optional ?? false,
      checklist: null, checklistId: null, startUserId: null, endUserId: null,
    };
  });
}

function _buildTutoPoste(etatOverride?: string): any {
  const now = new Date().toISOString();
  const of = ofs[0];
  const isTerminee = etatOverride === 'TERMINEE';
  return {
    id: 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4', index: 0, denomination: 'Opé. 1',
    etat: etatOverride ?? 'EN_COURS',
    dateDebut: now, dateFin: isTerminee ? now : null,
    quantitePrevue: of?.quantite ?? 250,
    quantiteProduite: isTerminee ? 250 : 0,
    quantiteProduitePoste: isTerminee ? 250 : 0,
    processesEtapeDone: _buildTutoPeos(),
    changementsLot: [], controles: [], controlesEvenement: [],
    controlesCalages: [], etapesCalages: [], consoLots: [],
    controleConso: [], integration: [], intervals: [],
    podInitial: null, podStart: null, podEnd: null,
    score: isTerminee ? 5 : 0,
    commentaire: isTerminee ? 'Production conforme, bon rendement.' : '',
    commentairePositif: '', commentaireNegatif: '',
    of, ligne: lignes[0] ?? null, userInfos: null, isActive: true, version: 1,
  };
}

function _buildTutoChangementsLot(): any[] {
  const now = new Date().toISOString();
  const posteId = 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4';
  return [
    { id: 'cl-farine', ofId: OF_BISCUITS, articleId: ART_FARINE, lotId: LOT_FARINE, lot: { id: LOT_FARINE, numero: 'FAR-2026-001' }, date: now, quantite: null, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[0] },
    { id: 'cl-beurre', ofId: OF_BISCUITS, articleId: ART_BEURRE, lotId: LOT_BEURRE, lot: { id: LOT_BEURRE, numero: 'BEU-2026-001' }, date: now, quantite: null, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[1] },
    { id: 'cl-sucre', ofId: OF_BISCUITS, articleId: ART_SUCRE, lotId: LOT_SUCRE, lot: { id: LOT_SUCRE, numero: 'SUC-2026-001' }, date: now, quantite: null, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[2] },
    { id: 'cl-oeufs', ofId: OF_BISCUITS, articleId: ART_OEUFS, lotId: LOT_OEUFS, lot: { id: LOT_OEUFS, numero: 'OEU-2026-001' }, date: now, quantite: null, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[3] },
    { id: 'cl-lait', ofId: OF_BISCUITS, articleId: ART_LAIT, lotId: LOT_LAIT, lot: { id: LOT_LAIT, numero: 'LAI-2026-001' }, date: now, quantite: 12.5, isStart: true, identifiant: '', posteId, createMouvement: false, takeStock: false, takePDC: false, makeMove: false, mouvementId: null, article: articles[4] },
  ];
}

/**
 * Build realistic bilan matière data for the cloture tutorial.
 * Used by the interceptor to populate the validation dialog with costs and percentages.
 */
export function _buildTutoBilans(quantiteProduite = 250): any[] {
  const posteId = 'a0a0a0a0-b1b1-4c2c-d3d3-e4e4e4e4e4e4';
  // Realistic consumption: slight losses on each ingredient
  const bilansData = [
    { articleId: ART_FARINE, ai: articleInfosFarine, unite: 'Kilogramme', lotId: LOT_FARINE,
      enPdc: 100, consoTheo: 100, consoReel: 98, restante: 2, coutUnit: 0.85, auto: false, perteN: 2, perteM: 5 },
    { articleId: ART_BEURRE, ai: articleInfosBeurre, unite: 'Kilogramme', lotId: LOT_BEURRE,
      enPdc: 62.5, consoTheo: 62.5, consoReel: 63.2, restante: null, coutUnit: 5.40, auto: true, perteN: 1, perteM: 3 },
    { articleId: ART_SUCRE, ai: articleInfosSucre, unite: 'Kilogramme', lotId: LOT_SUCRE,
      enPdc: 50, consoTheo: 50, consoReel: 50, restante: null, coutUnit: 0.72, auto: true, perteN: 1, perteM: 2 },
    { articleId: ART_OEUFS, ai: articleInfosOeufs, unite: 'Unite', lotId: LOT_OEUFS,
      enPdc: 25, consoTheo: 25, consoReel: 25.5, restante: null, coutUnit: 0.15, auto: true, perteN: 2, perteM: 5 },
    { articleId: ART_LAIT, ai: articleInfosLait, unite: 'Litre', lotId: LOT_LAIT,
      enPdc: 12.5, consoTheo: 12.5, consoReel: 12.5, restante: null, coutUnit: 0.95, auto: false, perteN: 1, perteM: 3 },
  ];

  return bilansData.map((b) => {
    const consoTheoriqueUnitaire = b.consoTheo / quantiteProduite;
    const pertesPourcent = b.consoTheo > 0 ? b.consoReel / b.consoTheo : 1;
    const retourPdc = b.enPdc - b.consoReel;
    const pertes = b.consoReel - b.consoTheo;
    return {
      id: `bilan-${b.articleId.substring(0, 8)}`,
      articleId: b.articleId,
      ofId: OF_BISCUITS,
      posteId,
      tiersId: null,
      articleInfos: b.ai,
      uniteDenomination: b.unite,
      enPdc: b.enPdc,
      consoTheoriqueUnitaire,
      consoTheorique: b.consoTheo,
      retourPdc: retourPdc > 0 ? retourPdc : 0,
      consoProd: b.consoReel,
      pertes: pertes > 0 ? pertes : 0,
      pertesPourcent,
      nonConforme: 0,
      coutUnitaire: b.coutUnit,
      coutMaterielProfondUnitaire: b.coutUnit,
      coutMOProfondUnitaire: 0,
      heuresProfondUnitaire: 0,
      auto: b.auto,
      state: Math.abs(pertesPourcent * 100 - 100) <= b.perteN ? 'valid' : 'warn',
      valid: true,
      percentNormal: b.perteN,
      percentMaxi: b.perteM,
      stocks: { lots: [], pdc: [] },
      dataPostes: {},
      dataReinte: {},
      bilanParPoste: [{
        posteId,
        reinte: true,
        consoLots: [{
          id: `lc-${b.articleId.substring(0, 8)}`,
          lotId: b.lotId,
          posteId,
          articleId: b.articleId,
          reinte: true,
          quantiteMiseEnPdc: b.enPdc,
          quantiteInitiale: b.enPdc,
          quantiteRestante: b.restante ?? (b.enPdc - b.consoReel),
          quantiteConsommee: b.consoReel,
          lot: lots.find((l: any) => l.id === b.lotId),
        }],
      }],
    };
  });
}

/**
 * Build a realistic BilanProductivite for the validation dialog.
 * Based on 250 kg produced, 2 operators, cadence 50 u/h.
 */
export function _buildTutoBilanProductivite(): any {
  const qtt = 250;
  const cadence = 50;
  const nbPersonnes = 2;
  const tempsCalage = 15; // min
  const tempsNettoyage = 20; // min
  const heuresTheorique = parseFloat(((qtt / cadence + tempsCalage / 60 + tempsNettoyage / 60) * nbPersonnes).toFixed(2));
  const heuresTravailles = 11.5;
  const salaireMoyen = 18;
  const productiviteTheorique = parseFloat((qtt / heuresTheorique).toFixed(1));
  const productiviteReel = parseFloat((qtt / heuresTravailles).toFixed(1));
  const productivitePourcent = parseFloat(((productiviteReel / productiviteTheorique) * 100).toFixed(1));
  const coutTotalTheorique = parseFloat((heuresTheorique * salaireMoyen).toFixed(2));
  const coutTotalPersonnel = parseFloat((heuresTravailles * salaireMoyen).toFixed(2));

  return {
    id: 'bp-tuto-001',
    cadence,
    nbPersonnes,
    tempsCalage,
    tempsNettoyage,
    heuresLigne: parseFloat((heuresTravailles / nbPersonnes).toFixed(2)),
    heuresTheorique,
    heuresTravailles,
    ecartHeuresTravailles: parseFloat((heuresTravailles - heuresTheorique).toFixed(2)),
    coutTotalPersonnel,
    coutTotalTheorique,
    ecartCoutTheorique: parseFloat(((coutTotalPersonnel / coutTotalTheorique - 1) * 100).toFixed(1)),
    ecartCoutTheoriquePourcent: parseFloat(((coutTotalPersonnel / coutTotalTheorique - 1) * 100).toFixed(1)),
    productiviteTheorique,
    productiviteReel,
    productivitePourcent,
    ecartProductiviteTheorique: parseFloat(((productiviteReel / productiviteTheorique - 1) * 100).toFixed(1)),
    qttProduite: qtt,
    salaireMoyen,
    surcoutTotal: parseFloat((coutTotalPersonnel - coutTotalTheorique).toFixed(2)),
    surcoutUnitaire: parseFloat(((coutTotalPersonnel - coutTotalTheorique) / qtt).toFixed(4)),
    surcoutUnitairePourcent: parseFloat(((coutTotalPersonnel / coutTotalTheorique - 1) * 100).toFixed(1)),
    coutUnitaireReel: parseFloat((coutTotalPersonnel / qtt).toFixed(4)),
    coutUnitaireTheorique: parseFloat((coutTotalTheorique / qtt).toFixed(4)),
    isActive: true, version: 1,
  };
}

/**
 * Build mock Travail entries for the hours display in the validation dialog.
 */
export function _buildTutoTravaux(): any[] {
  const now = new Date().toISOString().split('T')[0];
  return [
    { id: 'trav-001', ofId: OF_BISCUITS, ofNumero: 'OF2600001', userInfosId: 'ui-pierre', userInfos: { id: 'ui-pierre', nom: 'NICOLET', prenom: 'Pierre' }, date: now, duree: '06:00', defaultType: 'OF', isActive: true, version: 1 },
    { id: 'trav-002', ofId: OF_BISCUITS, ofNumero: 'OF2600001', userInfosId: 'ui-marie', userInfos: { id: 'ui-marie', nom: 'DUPONT', prenom: 'Marie' }, date: now, duree: '05:30', defaultType: 'OF', isActive: true, version: 1 },
  ];
}

/**
 * Build the Sucre cristal order line for CF003.
 * Used by the tutorial panel to inject the line after the "add ligne" step.
 */
export function _buildTutoCommandeLigneSucre(): any {
  return makeCfLigne('cfl-tuto-sucre', ART_SUCRE, 250, 0.72, 'KG', 'SUC-CRIST-25', CF_003, 0);
}

/** Transfert inter-sites du tuto (Entrepôt principal → Moulin de Provence Siège). */
const TE_TUTO_ID = 'aaee1100-bbbb-4ccc-8ddd-000000000001';
export function _buildTutoTransfert(etat = 'EN_REDACTION'): any {
  const ligne = (id: string, articleId: string, quantite: number, index: number) => ({
    id, transfertEntrepotId: TE_TUTO_ID, articleId,
    article: articles.find(a => a.id === articleId) ?? null,
    quantite, index,
  });
  return {
    id: TE_TUTO_ID,
    numero: 'TE2600001',
    etat,
    mode: 'AVEC_EXPEDITION',
    commentaire: 'Réassort mensuel du dépôt secondaire',
    adresseDepart: adresses[0], adresseDepartId: ADR_ENTREPOT,
    adresseArrive: adresses[1], adresseArriveId: ADR_MOULIN,
    adresseZoneTransfert: null, adresseZoneTransfertId: null,
    adresseZoneDechargement: null, adresseZoneDechargementId: null,
    createur: null, createurId: null,
    validator: null, validatorId: null,
    annulateur: null, annulateurId: null,
    soldeur: null, soldeurId: null,
    dateCreation: new Date('2026-06-08T08:30:00').toISOString(),
    dateValidation: null, dateAnnulation: null, dateSoldee: null, dateFermeture: null,
    documentListeId: null,
    lignes: [
      ligne('te-ligne-far', ART_FARINE, 200, 0),
      ligne('te-ligne-suc', ART_SUCRE, 100, 1),
    ],
    palettes: [],
  };
}

/** Inventaire annuel du tuto (2 zones : Entrepôt principal + Chambre froide). */
const INV_TUTO_ID = 'bbff2200-cccc-4ddd-8eee-000000000001';
export function _buildTutoInventaire(): any {
  return {
    id: INV_TUTO_ID,
    denomination: 'Inventaire annuel 2026',
    dateDebut: new Date('2026-06-09T07:00:00').toISOString(),
    dateFin: null,
    date: new Date('2026-06-09T07:00:00').toISOString(),
    adresseId: ADR_ENTREPOT,
    type: 'RAZ_OTHER',
    articles: [],
    bilans: [],
    zones: [
      { id: 'inv-zone-1', denomination: 'Entrepôt principal', inventaireId: INV_TUTO_ID, over: null, comptages: [], nbComptageUser: 0 },
      { id: 'inv-zone-2', denomination: 'Chambre froide', inventaireId: INV_TUTO_ID, over: null, comptages: [], nbComptageUser: 3 },
    ],
  };
}

/**
 * Graphe de traçabilité du lot de biscuits (tuto gestion-tracabilite, onglet
 * traçabilité de la fiche lot). Chaîne amont→aval :
 *   Fournisseur → (réception) → lots MP (farine, sucre) → (conso) → OF
 *   → (production) → lot biscuits (ancre) → (expédition) → client.
 */
/**
 * Graphe de traçabilité « riche » (vue cards V2, endpoint /tracabilite-graph).
 * Chaîne complète du lot de biscuits démo : fournisseur → réception →
 * composants → fabrication (OF) → expédition → client.
 */
export function _buildTutoTraceGraphV2(): any {
  const d = (n: number, h = 9) => _workDay(n, h);
  return {
    focusId: 'g-fab',
    nodes: [
      { id: 'g-fourn', type: 'fournisseur', label: PROSPECT_PROFILE.supplier, sublabel: 'Fournisseur', icon: 'agriculture',
        lines: [{ id: 'gl-cf1', label: 'CF2600001', sublabel: 'Commande fournisseur', relevant: true, date: d(-5), link: { kind: 'commande-fournisseur', id: CF_001 } }],
        documents: [{ kind: 'commande-fournisseur', label: 'Bon de commande CF2600001' }] },
      { id: 'g-recep', type: 'reception', label: 'Réception', sublabel: 'Déchargement du ' + d(-4).slice(0, 10), icon: 'call_received',
        lines: [
          { id: 'gl-rec-far', label: 'Farine de blé T65', sublabel: 'BL-2026-0042 · 500 kg', relevant: true, date: d(-4) },
          { id: 'gl-rec-beu', label: 'Beurre doux 82%', sublabel: 'BL-2026-0042 · 100 kg', relevant: true, date: d(-4) },
        ],
        documents: [{ kind: 'blf-info', label: 'BL fournisseur BL-2026-0042' }] },
      { id: 'g-compo', type: 'composant', label: 'Matières premières', sublabel: '3 lots consommés', icon: 'category',
        lines: [
          { id: LOT_FARINE, label: 'FAR-2026-001', sublabel: 'Farine de blé T65 · 100 kg', relevant: true, date: d(-3), link: { kind: 'lot', id: LOT_FARINE } },
          { id: LOT_BEURRE, label: 'BEU-2026-001', sublabel: 'Beurre doux 82% · 62,5 kg', relevant: true, date: d(-3), link: { kind: 'lot', id: LOT_BEURRE } },
          { id: LOT_SUCRE, label: 'SUC-2026-001', sublabel: 'Sucre cristal · 50 kg', relevant: true, date: d(-3), link: { kind: 'lot', id: LOT_SUCRE } },
        ] },
      { id: 'g-fab', type: 'fabrication', label: 'OF2600001', sublabel: 'Ligne Biscuits', icon: 'factory', ligne: 'Ligne Biscuits',
        ofMeta: { numero: 'OF2600001', ligne: 'Ligne Biscuits', dateDebut: d(-3), dateFin: d(-3, 13), qty: '250 kg' },
        lines: [{ id: LOT_BISCUITS, label: 'BIS-2026-001', sublabel: 'Biscuits sablés du Moulin · 250 kg', relevant: true, date: d(-3, 13), link: { kind: 'lot', id: LOT_BISCUITS } }],
        documents: [{ kind: 'of-dossier', label: 'Dossier de fabrication OF2600001' }, { kind: 'lot-etiquette', label: 'Étiquette lot BIS-2026-001' }] },
      { id: 'g-exp', type: 'expedition', label: 'Expédition', sublabel: 'BL2600001', icon: 'local_shipping',
        lines: [{ id: 'gl-bl1', label: 'BL2600001', sublabel: 'CC2600001 · 100 kg', relevant: true, date: d(-1, 16) }],
        documents: [{ kind: 'bl-client', label: 'Bon de livraison BL2600001' }] },
      { id: 'g-client', type: 'client', label: PROSPECT_PROFILE.client, sublabel: 'Client', icon: 'storefront',
        lines: [{ id: 'gl-cc1', label: 'CC2600001', sublabel: 'Commande client', relevant: true, date: d(-1, 16), link: { kind: 'commande-client', id: CC_001 } }] },
    ],
    edges: [
      { id: 'e1', source: 'g-fourn', target: 'g-recep', qtyLabel: '600 kg' },
      { id: 'e2', source: 'g-recep', target: 'g-compo', qtyLabel: '600 kg' },
      { id: 'e3', source: 'g-compo', target: 'g-fab', qtyLabel: '212,5 kg' },
      { id: 'e4', source: 'g-fab', target: 'g-exp', qtyLabel: '100 kg' },
      { id: 'e5', source: 'g-exp', target: 'g-client', qtyLabel: '100 kg' },
    ],
  };
}

export function _buildTutoTracaGraph(): any {
  const node = (id: string, idx: number, type: string, properties: any) =>
    ({ id, node: idx, name: id, drop: 0, type, properties });
  const link = (id: number, source: string, target: string, value: number, type: string, articleId: string) =>
    ({ id, source, target, value, type, properties: { quantite: value, articleId } });
  return {
    date: new Date('2026-06-09T16:00:00').toISOString(),
    nodes: [
      node('n-tiers-moulin', 0, 'Tiers', { tiersId: TIERS_MOULIN, tiersDenomination: PROSPECT_PROFILE.supplier }),
      node('n-lot-far', 1, 'Lot', { lotId: LOT_FARINE, articleId: ART_FARINE, lotNumero: 'FAR-2026-001' }),
      node('n-lot-suc', 2, 'Lot', { lotId: LOT_SUCRE, articleId: ART_SUCRE, lotNumero: 'SUC-2026-001' }),
      node('n-of', 3, 'Of', { ofId: OF_BISCUITS, articleId: ART_BISCUITS, ofNumero: 'OF2600001' }),
      node('n-lot-bis', 4, 'Lot', { lotId: LOT_BISCUITS, articleId: ART_BISCUITS, lotNumero: 'BIS-2026-001' }),
      node('n-tiers-boulang', 5, 'Tiers', { tiersId: TIERS_BOULANG, tiersDenomination: PROSPECT_PROFILE.client }),
    ],
    links: [
      link(1, 'n-tiers-moulin', 'n-lot-far', 500, 'Reception', ART_FARINE),
      link(2, 'n-tiers-moulin', 'n-lot-suc', 300, 'Reception', ART_SUCRE),
      link(3, 'n-lot-far', 'n-of', 200, 'Consommation', ART_FARINE),
      link(4, 'n-lot-suc', 'n-of', 100, 'Consommation', ART_SUCRE),
      link(5, 'n-of', 'n-lot-bis', 250, 'Production', ART_BISCUITS),
      link(6, 'n-lot-bis', 'n-tiers-boulang', 120, 'Expedition', ART_BISCUITS),
    ],
  };
}

/**
 * Analyse marge & prix du PF (tuto popups d'analyse). POST
 * /article-marge-analysis/:id. Données cohérentes : PF vendu ~15 €/kg, coût
 * ~7 €/kg, marge ~53 %, 3 clients dont le client du profil prospect.
 */
export function _buildTutoMargeAnalysis(): any {
  const months = ['2025-07','2025-08','2025-09','2025-10','2025-11','2025-12','2026-01','2026-02','2026-03','2026-04','2026-05','2026-06'];
  const monthly = months.map((ym, i) => {
    const qty = 80 + (i % 4) * 18;
    const prix = 14.4 + (i * 0.06);
    const cout = 7.0;
    return {
      yearMonth: ym, qty, caNet: Math.round(qty * prix), caBrut: Math.round(qty * prix * 1.07),
      coutRetenu: Math.round(qty * cout), margeRetenue: Math.round(qty * (prix - cout)),
      prixMoyenUV: +prix.toFixed(2), coutMoyenUV: cout, margeMoyenneUV: +(prix - cout).toFixed(2),
      tauxMargeRetenu: +(((prix - cout) / prix) * 100).toFixed(1), nbLignes: 3,
    };
  });
  const mkClient = (tiersId: string, tiersName: string, qty: number, prix: number, tarif: number) => ({
    tiersId, tiersName, tierGroupIds: [], tierGroupNames: [],
    qty, qtyUG: qty, caBrut: Math.round(qty * prix * 1.07), caNet: Math.round(qty * prix),
    remisePied: Math.round(qty * prix * 0.07), coutMatiere: Math.round(qty * 5), coutMO: Math.round(qty * 2),
    coutTotal: Math.round(qty * 7), coutRetenu: Math.round(qty * 7), margeRetenue: Math.round(qty * (prix - 7)),
    tauxMargeRetenu: +(((prix - 7) / prix) * 100).toFixed(1), prixMoyenUV: prix, coutMoyenUV: 7,
    margeMoyenneUV: +(prix - 7).toFixed(2), tarifCatalogueUV: tarif, tarifCatalogueLevel: 'tiers' as const,
    ecartCataloguePct: +(((prix - tarif) / tarif) * 100).toFixed(1),
    remisesActives: [{ label: 'Remise volume', type: 'POURCENTAGE' as const, valeur: 5, scope: 'ligne' }],
    nbFactures: Math.round(qty / 50), nbLignes: Math.round(qty / 40),
  });
  return {
    filter: { articleId: ART_BISCUITS, derniersMois: 12, includeMO: true, includeRemisePied: true },
    article: { id: ART_BISCUITS, code: 'PF-BIS-001', denomination: PROSPECT_PROFILE.product, uniteVente: 'Kilogramme', uniteGestion: 'Kilogramme', coefUVtoUG: 1 },
    perimetre: 'matiere_et_mo',
    kpis: {
      qty: 1240, qtyUG: 1240, uniteVente: 'Kilogramme', uniteGestion: 'Kilogramme',
      caBrut: 19840, caNet: 18600, remisePied: 1240, coutMatiere: 6200, coutMO: 2480,
      coutTotal: 8680, coutRetenu: 8680, margeRetenue: 9920, tauxMargeRetenu: 53.3, coefficient: 2.14,
      prixMoyenUV: 15.0, prixMedianUV: 14.8, prixMinUV: 12.5, prixMaxUV: 17.2, prixEcartType: 1.3,
      margeMoyenneUV: 8.0, coutMoyenUV: 7.0, nbClients: 3, nbFactures: 24, nbLignes: 41, remisePiedPctMoyen: 6.2,
    },
    clients: [
      mkClient(TIERS_BOULANG, PROSPECT_PROFILE.client, 620, 15.0, 15.5),
      mkClient(TIERS_SUPER, 'Supermarché FraisMalin', 480, 14.0, 15.5),
      mkClient('cafe0001-aaaa-4bbb-cccc-dddddddddddd', 'Café des Arts', 140, 16.4, 15.5),
    ],
    groupes: [
      { tierGroupId: null, tierGroupName: 'Grande distribution', qty: 480, caNet: 6720, prixMoyenUV: 14.0, prixMedianUV: 14.0, prixMinUV: 12.5, prixMaxUV: 15.0, margeMoyenneUV: 7.0, tauxMargeRetenu: 50.0, nbClients: 1, nbLignes: 12 },
      { tierGroupId: null, tierGroupName: 'Détaillants', qty: 760, caNet: 11880, prixMoyenUV: 15.6, prixMedianUV: 15.5, prixMinUV: 14.0, prixMaxUV: 17.2, margeMoyenneUV: 8.6, tauxMargeRetenu: 55.1, nbClients: 2, nbLignes: 29 },
    ],
    distribution: [
      { minUV: 12, maxUV: 13.5, qty: 180, caNet: 2340, nbLignes: 6, nbClients: 1 },
      { minUV: 13.5, maxUV: 15, qty: 520, caNet: 7280, nbLignes: 17, nbClients: 2 },
      { minUV: 15, maxUV: 16.5, qty: 400, caNet: 6200, nbLignes: 13, nbClients: 2 },
      { minUV: 16.5, maxUV: 18, qty: 140, caNet: 2580, nbLignes: 5, nbClients: 1 },
    ],
    monthly,
    lignes: [
      { factureId: 'fc-ma-1', factureNumero: 'FC2600001', factureDate: '2026-06-02', isAvoir: false, tiersId: TIERS_BOULANG, tiersName: PROSPECT_PROFILE.client, lotId: LOT_BISCUITS, lotNumero: 'BIS-2026-001', quantite: 120, quantiteUG: 120, prixNetUV: 15.0, prixBrutUV: 16.05, caBrut: 1926, remisePied: 126, caNet: 1800, coutMatiereUnitUG: 5, coutMOUnitUG: 2, coutMatiere: 600, coutMO: 240, coutRetenu: 840, margeRetenue: 960 },
      { factureId: 'fc-ma-2', factureNumero: 'FC2600002', factureDate: '2026-05-18', isAvoir: false, tiersId: TIERS_SUPER, tiersName: 'Supermarché FraisMalin', lotId: LOT_BISCUITS, lotNumero: 'BIS-2026-001', quantite: 200, quantiteUG: 200, prixNetUV: 14.0, prixBrutUV: 15.0, caBrut: 3000, remisePied: 200, caNet: 2800, coutMatiereUnitUG: 5, coutMOUnitUG: 2, coutMatiere: 1000, coutMO: 400, coutRetenu: 1400, margeRetenue: 1400 },
    ],
    pricing: {
      coutCurrent: { matiere: 5.0, mo: 2.0, total: 7.0, retenu: 7.0, source: 'ventes', sourceLabel: 'Coût observé sur les ventes', moSource: 'realise', moSourceLabel: 'MO réalisée' },
      prixStats: { moyenUV: 15.0, medianUV: 14.8, p25UV: 13.8, p75UV: 16.0, minUV: 12.5, maxUV: 17.2, ecartType: 1.3 },
      tarifGeneriqueUV: 15.5,
      prixCibleParTauxMarge: [{ tauxCible: 30, prixUV: 10.0 }, { tauxCible: 40, prixUV: 11.67 }, { tauxCible: 50, prixUV: 14.0 }, { tauxCible: 60, prixUV: 17.5 }],
      conseils: [
        'Le prix moyen (15,00 €) dégage déjà une marge de 53 %, au-dessus de votre cible de 30 %.',
        'Supermarché FraisMalin achète 9,7 % sous le tarif catalogue : marge de négociation à la prochaine commande.',
        'Café des Arts paie 16,40 € (marge 57 %) : votre meilleur client en rentabilité.',
      ],
    },
    meta: { computedAt: new Date('2026-06-11T09:00:00').toISOString(), durationMs: 42, nbFacturesScanned: 24, nbLignesScanned: 41 },
  };
}

/**
 * Analyse achats d'une MP (farine). POST /article-achat-analysis/:id.
 * Farine achetée à 2 fournisseurs, ~0,85 €/kg, conformité 98 %.
 */
export function _buildTutoAchatAnalysis(): any {
  const months = ['2025-07','2025-08','2025-09','2025-10','2025-11','2025-12','2026-01','2026-02','2026-03','2026-04','2026-05','2026-06'];
  const monthly = months.map((ym, i) => {
    const qty = 380 + (i % 3) * 60; const prix = 0.80 + i * 0.005;
    return { yearMonth: ym, qtyRecueUG: qty, coutAchat: +(qty * prix).toFixed(2), prixMoyenUG: +prix.toFixed(3), prixMinUG: +(prix - 0.03).toFixed(3), prixMaxUG: +(prix + 0.04).toFixed(3), nbReceptions: 1, nbFournisseurs: 1 };
  });
  const mkFourn = (tiersId: string, tiersName: string, qty: number, prix: number, tarif: number, conf: number, delai: number, rangV: number, rangC: number) => ({
    tiersId, tiersName, tierGroupIds: [], tierGroupNames: [], uniteAchat: 'Sac 25kg', coefAchatToUG: 25,
    qtyRecueUA: Math.round(qty / 25), qtyRecueUG: qty, qtyConformeUG: Math.round(qty * conf / 100), qtyNonConformeUG: Math.round(qty * (100 - conf) / 100), qtyCommandeeUG: qty,
    coutAchat: +(qty * prix).toFixed(2), prixMoyenUA: +(prix * 25).toFixed(2), prixMoyenUG: prix, tarifCatalogueUA: +(tarif * 25).toFixed(2), tarifCatalogueUG: tarif,
    ecartCataloguePct: +(((prix - tarif) / tarif)).toFixed(3), ecartGlobalPct: +(((prix - 0.85) / 0.85)).toFixed(3), premierPrixUG: +(prix - 0.04).toFixed(3), dernierPrixUG: prix, evolutionPrixPct: 0.045,
    tauxConformite: conf / 100, delaiLivraisonMoyenJours: delai, nbCommandes: 6, nbReceptions: 7, nbLots: 7, derniereReception: '2026-06-05',
    volumeMoyenParCommandeUG: Math.round(qty / 6), volumeMoyenParReceptionUG: Math.round(qty / 7), frequenceMensuelleCommandes: 0.5, tauxRealisationCommande: 0.99,
    partVolumeConformeUG: conf / 100, partCoutAchat: rangC === 1 ? 0.65 : 0.35, rangVolumeUG: rangV, rangCoutAchat: rangC, scoreFiabilite: conf / 100,
    conditions: { reductionPct: 0.03, francoQuantite: 500, francoPrix: null, typeReglement: 'Virement', delaiReglement: '30 jours', delaiReglementJour: 30 }, multipleAchat: 25,
  });
  return {
    filter: { articleId: ART_FARINE, derniersMois: 12 },
    article: { id: ART_FARINE, code: 'MP-FAR-001', denomination: PROSPECT_PROFILE.ingredients[0], uniteGestion: 'Kilogramme' },
    kpis: {
      qtyRecueUG: 5000, qtyConformeUG: 4900, qtyNonConformeUG: 100, qtyCommandeeUG: 5050,
      coutAchatTotal: 4250, coutAchatConforme: 4165, prixMoyenUG: 0.85, prixMedianUG: 0.84, prixMinUG: 0.80, prixMaxUG: 0.92, prixEcartTypeUG: 0.03,
      prixDernierMoisUG: 0.88, evolutionPrixPct: 0.045, tauxConformite: 0.98, delaiLivraisonMoyenJours: 3.2, partTopFournisseur: 0.65, herfindahl: 0.55,
      tarifGeneriqueUG: 0.88, nbFournisseurs: 2, nbCommandes: 12, nbReceptions: 14, nbLots: 14, premiereCommande: '2025-07-03', derniereReception: '2026-06-05',
      volumeMoyenParCommandeUG: 417, volumeMoyenParReceptionUG: 357, frequenceMensuelleCommandes: 1.0, tauxRealisationCommande: 0.99,
    },
    fournisseurs: [
      mkFourn(TIERS_MOULIN, PROSPECT_PROFILE.supplier, 3250, 0.83, 0.88, 99, 2.8, 1, 1),
      mkFourn('ferme001-aaaa-4bbb-cccc-dddddddddddd', 'Ferme des Hauts Prés', 1750, 0.89, 0.88, 96, 4.0, 2, 2),
    ],
    groupes: [{ tierGroupId: null, tierGroupName: 'Meuniers', qtyRecueUG: 5000, coutAchat: 4250, prixMoyenUG: 0.85, prixMinUG: 0.80, prixMaxUG: 0.92, nbFournisseurs: 2, nbReceptions: 14 }],
    distribution: [
      { minUG: 0.78, maxUG: 0.83, qtyUG: 1800, coutAchat: 1458, nbReceptions: 5, nbFournisseurs: 1 },
      { minUG: 0.83, maxUG: 0.88, qtyUG: 2200, coutAchat: 1881, nbReceptions: 6, nbFournisseurs: 2 },
      { minUG: 0.88, maxUG: 0.93, qtyUG: 1000, coutAchat: 911, nbReceptions: 3, nbFournisseurs: 1 },
    ],
    monthly,
    monthlyByTiers: [],
    receptions: [
      { receptionId: 'rec-1', date: '2026-06-05', commandeId: CF_001, commandeNumero: 'CF2600001', bonLivraisonNumero: 'BL-F-026', tiersId: TIERS_MOULIN, tiersName: PROSPECT_PROFILE.supplier, lotFournisseurId: null, lotFournisseurNumero: 'MIN-2606', uniteAchat: 'Sac 25kg', coefAchatToUG: 25, qtyUA: 20, qtyConformeUA: 20, qtyNonConformeUA: 0, qtyUG: 500, qtyConformeUG: 500, qtyNonConformeUG: 0, prixUA: 20.75, prixUG: 0.83, coutLigne: 415 },
    ],
    nego: {
      volumeAnnualiseUG: 5000, prixActuelUG: 0.85,
      meilleurFournisseur: { tiersId: TIERS_MOULIN, tiersName: PROSPECT_PROFILE.supplier, prixUG: 0.83, economieAnnuelleEur: 100 },
      tarifGeneriqueUG: 0.88, economieAuTarifGenerique: 150,
      conseils: [
        PROSPECT_PROFILE.supplier + ' propose le meilleur prix (0,83 €/kg) : concentrer les volumes y économiserait ~100 €/an.',
        'Vous achetez 3,4 % sous le tarif catalogue : bonne position de négociation.',
        'Ferme des Hauts Prés est plus chère (+7 %) et moins fiable (96 % conforme) : à arbitrer.',
      ],
    },
    produitsConsommateurs: [
      { produitFiniId: ART_BISCUITS, produitFiniName: PROSPECT_PROFILE.product, produitFiniCode: 'PF-BIS-001', qtyConsommeeUG: 4800, partPct: 0.96, nbOfs: 6, derniereProductionDate: '2026-06-04' },
    ],
    meta: { computedAt: new Date('2026-06-11T09:00:00').toISOString(), durationMs: 38, nbCommandesScanned: 12, nbReceptionsScanned: 14 },
  };
}

/**
 * Analyse fabrication du PF (biscuits). POST /article-fabrication-analysis/:id.
 * Coût de revient ~7 €/kg (5 matière + 2 MO), 6 OF, composants farine/beurre/sucre.
 */
export function _buildTutoFabricationAnalysis(): any {
  const months = ['2026-01','2026-02','2026-03','2026-04','2026-05','2026-06'];
  const monthly = months.map((ym, i) => ({ yearMonth: ym, qtyProduiteUG: 240 + i * 10, coutMatiereUnitaire: +(4.9 + i * 0.03).toFixed(2), coutMOUnitaire: 2.0, coutTotalUnitaire: +(6.9 + i * 0.03).toFixed(2), heuresTotal: 12 + i, cadenceUGParH: 120, nbLots: 1, nbOfs: 1 }));
  const mkComp = (articleId: string, articleName: string, code: string, theo: number, reel: number, coutMat: number, part: number) => ({
    articleId, articleName, articleCode: code, uniteGestion: 'Kilogramme', familleName: 'Matières premières', sousFamilleName: 'Ingrédients',
    qtyTheoriqueUG: theo, qtyReelleUG: reel, ecartPct: +((reel - theo) / theo).toFixed(3), coutMatiereUnitaire: coutMat, coutMOProfondUnitaire: 0,
    coutTotal: +(reel * coutMat).toFixed(2), partPct: part, nbOfs: 6,
    parOf: [
      { ofId: OF_BISCUITS, ofNumero: 'OF2600001', ligneNom: 'Ligne Biscuits', dateFin: '2026-06-04', qtyOfProduite: 250, qtyTheoriqueUG: theo / 6, qtyReelleUG: reel / 6, ecartPct: 0.02, coutTotal: +(reel * coutMat / 6).toFixed(2) },
    ],
  });
  return {
    filter: { articleId: ART_BISCUITS, derniersMois: 12 },
    article: { id: ART_BISCUITS, code: 'PF-BIS-001', denomination: PROSPECT_PROFILE.product, uniteGestion: 'Kilogramme' },
    kpis: {
      qtyProduiteUG: 1500, nbOfs: 6, nbLots: 6, coutMatiereUnitaire: 5.0, coutMOUnitaire: 2.0, coutTotalUnitaire: 7.0, coutTotalCumule: 10500,
      heuresUnitaire: 0.0083, evolutionCoutPct: 0.023, ecartConsoPct: 0.031, concentrationLignePct: 1.0, cadenceMoyenneUGParH: 120, premiereProduction: '2026-01-12', derniereProduction: '2026-06-04',
    },
    composantsDirects: [
      mkComp(ART_FARINE, PROSPECT_PROFILE.ingredients[0], 'MP-FAR-001', 600, 618, 0.85, 0.52),
      mkComp(ART_BEURRE, PROSPECT_PROFILE.ingredients[1], 'MP-BEU-002', 180, 184, 5.40, 0.33),
      mkComp(ART_SUCRE, PROSPECT_PROFILE.ingredients[2], 'MP-SUC-003', 150, 152, 0.72, 0.15),
    ],
    composantsProfonds: [],
    composantsFournisseurs: [{ tiersId: TIERS_MOULIN, tiersName: PROSPECT_PROFILE.supplier, coutPropage: 5250, partPct: 0.52, nbComposants: 1 }],
    monthly,
    monthlyByLigne: [],
    distributionCouts: [
      { minCout: 6.5, maxCout: 6.9, nbOfs: 2, qtyUG: 480, coutCumule: 3264 },
      { minCout: 6.9, maxCout: 7.3, nbOfs: 3, qtyUG: 760, coutCumule: 5320 },
      { minCout: 7.3, maxCout: 7.7, nbOfs: 1, qtyUG: 260, coutCumule: 1950 },
    ],
    parLigne: [{ ligneId: 'lg-biscuits', ligneNom: 'Ligne Biscuits', qtyProduiteUG: 1500, partPct: 1.0, nbOfs: 6, heuresTotal: 84, cadenceUGParH: 120 }],
    productionTimeline: months.map((ym, i) => ({ date: ym + '-15', qtyProduiteUG: 240 + i * 10, coutTotalUnitaire: +(6.9 + i * 0.03).toFixed(2) })),
    granularite: 'mois',
    ofs: [
      { ofId: OF_BISCUITS, ofNumero: 'OF2600001', ligneId: 'lg-biscuits', ligneNom: 'Ligne Biscuits', dateDebut: '2026-06-02', dateFin: '2026-06-04', qtyProduiteUG: 250, coutMatiereUnitaire: 5.0, coutMOUnitaire: 2.0, coutTotalUnitaire: 7.0, coutTotal: 1750, heuresTotal: 14, cadenceUGParH: 120, nbLots: 1, isOutlier: false },
    ],
    simulator: {
      volumeAnnualiseUG: 3000, coutMatiereActuel: 5.0, coutMOActuel: 2.0, coutAnnuelActuel: 21000,
      topComposant: { articleId: ART_FARINE, articleName: PROSPECT_PROFILE.ingredients[0], coutAnnuel: 10500 },
      ecartConsoActuelPct: 0.031,
      conseils: [
        'La farine pèse 52 % du coût matière : -5 % sur son prix d\'achat ferait gagner ~0,22 €/kg de PF.',
        'Écart de consommation +3,1 % vs théorique : une perte matière à investiguer en production.',
        'Coût de revient stable (~7 €/kg) ; marge confortable au prix de vente moyen de 15 €/kg.',
      ],
    },
    meta: { computedAt: new Date('2026-06-11T09:00:00').toISOString(), durationMs: 51, nbOfsScanned: 6, nbLotsScanned: 6 },
  };
}

/**
 * Tableau de bord de démo (tuto). GET /dashboard/type/:type → config (3 widgets),
 * GET /widget/type/:type → données par widget. Widgets choisis pour des formes
 * de données simples et sans risque de crash : valeur stock par famille,
 * quantité produite par période, lots par état.
 */
export function _buildTutoDashboard(): any {
  const w = (id: string, x: number, y: number, ww: number, h: number, type: string, mobileIndex: number, color: number) =>
    ({ id, x, y, w: ww, h, type, mobileIndex, color, isActive: true, version: 1 });
  // La grille du dashboard fait 4 colonnes en desktop (DESKTOP_COLUMN = 4) :
  // pleine largeur = w4, demi = w2. Layout aligné sur un vrai TDB :
  // « Activité à venir » (PLANNING) pleine largeur en haut, puis une rangée
  // CA / Valeur stock (donuts), puis CA par période / CA cumulé (graphes).
  return {
    id: 'dash-tuto-home', width: 4, type: 'HOME', userId: null, denomination: 'Tableau de bord',
    isActive: true, version: 1,
    widgets: [
      w('wg-plan', 0, 0, 4, 2, 'PLANNING',               0, 0),
      w('wg-rpc',  0, 2, 2, 2, 'REPARTITION_PAR_CLIENT', 1, 1),
      w('wg-vpf',  2, 2, 2, 2, 'VALEUR_PAR_FAMILLE',     2, 2),
      w('wg-cap',  0, 4, 2, 2, 'CA_PAR_PERIODE',         3, 3),
      w('wg-cac',  2, 4, 2, 2, 'CA_CUMULE',              4, 4),
    ],
  };
}

// Série mensuelle de CA (fév→juin 2026) réutilisée par CA_PAR_PERIODE et CA_CUMULE.
function _tutoCaSerie(): any[] {
  const pts = [
    ['2026-02-01', 18200], ['2026-03-01', 21500], ['2026-04-01', 19800],
    ['2026-05-01', 24300], ['2026-06-01', 25955],
  ];
  return pts.map(([date, montant]) => ({
    date, montant, montant_echeance: montant, montant_du: 0, montant_du_echeance: 0,
    factureIds: [], commandeIds: [], tiersDenomination: '',
  }));
}

export function _buildTutoWidget(type: string): any {
  switch (type) {
    case 'VALEUR_PAR_FAMILLE':
      return { type, data: {
        byFamille: [
          { denomination: 'Produits finis', total: 9800 },
          { denomination: 'Matières premières', total: 4250 },
        ],
        bySousFamille: [],
        byArticle: [
          { denomination: PROSPECT_PROFILE.product, total: 9800 },
          { denomination: PROSPECT_PROFILE.ingredients[0], total: 2550 },
          { denomination: PROSPECT_PROFILE.ingredients[1], total: 1080 },
          { denomination: PROSPECT_PROFILE.ingredients[2], total: 620 },
        ],
        byEntrepot: [{ denomination: PROSPECT_PROFILE.company + ' - Siège', total: 14050 }],
        details: [],
        total: 14050,
      } };
    case 'REPARTITION_PAR_CLIENT': {
      // Le widget RE-CALCULE byClient/total à partir des `details` filtrés par
      // période (exercice en cours) : il faut donc des lignes datées en 2026.
      const det = (tiers: string, montant: number, cout: number, date: string) => ({
        tiersDenomination: tiers, tiersGroupe: 'Clients',
        articleDenomination: PROSPECT_PROFILE.product, familleDenomination: 'Produits finis',
        lotNumero: '', quantite: 0, quantiteUG: 0, prixUnitaire: 0,
        coutUnitaire: cout, uniteVente: 'kg', uniteGestion: 'kg', date,
        montantHT: montant, montantBrut: montant, remisePied: 0,
        coutMateriel: cout, coutMO: 0, marginTracable: true,
      });
      const details = [
        det(PROSPECT_PROFILE.client, 9400, 5800, '2026-05-12'),
        det(PROSPECT_PROFILE.client, 8000, 4900, '2026-06-03'),
        det('Biocoop Centre',        4200, 2600, '2026-05-20'),
        det('Épicerie du Marché',    2300, 1450, '2026-06-05'),
        det('Halle Bio',             1300,  820, '2026-04-28'),
        det('Marché Frais',           755,  470, '2026-05-30'),
      ];
      return { type, data: {
        byClient: [], byArticle: [], byFamille: [], byGroupeTiers: [],
        details, total: 25955, caTracable: 25955,
      } };
    }
    case 'CA_PAR_PERIODE':
    case 'CA_CUMULE':
      return { type, data: { data: _tutoCaSerie() } };
    case 'QUANTITE_PRODUITE_PERIODE':
      return { type, data: { data: [
        { date: '2026-03-01', ligneDenomination: 'Ligne Biscuits', quantiteProduite: 240 },
        { date: '2026-04-01', ligneDenomination: 'Ligne Biscuits', quantiteProduite: 260 },
        { date: '2026-05-01', ligneDenomination: 'Ligne Biscuits', quantiteProduite: 250 },
        { date: '2026-06-01', ligneDenomination: 'Ligne Biscuits', quantiteProduite: 250 },
      ] } };
    case 'LOT_PAR_ETAT':
      return { type, data: { dluo: [], dlcImminente: [], nc: [] } };
    default:
      return { type, data: {} };
  }
}

/**
 * Historique des coûts d'un lot de farine (tuto évolution des coûts).
 * GET /cost/lotId/:id. Réception à 0,85 €/kg puis revalorisation manuelle à
 * 0,92 €/kg (hausse fournisseur).
 */
export function _buildTutoCostHistory(): any[] {
  const farineArt = articles.find(a => a.id === ART_FARINE) ?? null;
  const farineLot = lots.find(l => l.id === LOT_FARINE) ?? null;
  return [
    {
      id: 'cost-far-1', ofId: null, commandeId: CF_001, article: farineArt, articleId: ART_FARINE,
      lot: farineLot, lotId: LOT_FARINE, coutPersonnelUnitaire: 0, coutMaterielUnitaire: 0.85,
      coutMaterielProfond: 0.85, coutMOProfond: 0, date: '2026-05-10T08:00:00',
      sourceType: 'RECEPTION', sourceId: CF_001, sourceLabel: 'Réception CF2600001', commentaire: null,
      isActive: true, version: 1,
    },
    {
      id: 'cost-far-2', ofId: null, commandeId: null, article: farineArt, articleId: ART_FARINE,
      lot: farineLot, lotId: LOT_FARINE, coutPersonnelUnitaire: 0, coutMaterielUnitaire: 0.92,
      coutMaterielProfond: 0.92, coutMOProfond: 0, date: '2026-06-08T09:30:00',
      sourceType: 'REVALORISATION', sourceId: null, sourceLabel: 'Revalorisation manuelle',
      userOverride: true, commentaire: 'Hausse tarif fournisseur +8 %', isActive: true, version: 1,
    },
  ];
}

/**
 * Impact cascade d'une revalorisation du coût de la farine : les OF et lots de
 * produits finis qui la consomment en aval. GET /calcul_cascade/lot/:id/impact.
 */
export function _buildTutoCascadeImpact(): any {
  return {
    lots: [{ id: LOT_BISCUITS, numero: 'BIS-2026-001', articleId: ART_BISCUITS }],
    ofs: [{ id: OF_BISCUITS, numero: 'OF2600001', articleId: ART_BISCUITS, dateFin: '2026-06-04' }],
  };
}

/**
 * Flat map of all demo entities by type, for direct programmatic access.
 */
export const TUTORIAL_DEMO_DATA = {
  articles,
  articleInfos: articleInfosList,
  specificationMasters,
  tiers,
  adresses,
  lots,
  commandesFournisseur,
  commandesClient,
  customEnums,
  nomenclatures,
  nomenclatureLignes,
  processEtapes,
  controles,
  controlePod,
  controlePods,
  lignes,
  ofs,
  activityInfos,
} as const;

// ---------------------------------------------------------------------------
// Mock route registry
// ---------------------------------------------------------------------------

const UUID = '[0-9a-z-]{36}';

/**
 * Registry of mock GET responses keyed by URL pattern.
 * Evaluated top-to-bottom; first match wins.
 */
// ---------------------------------------------------------------------------
// Progress-aware helpers for SEM/SE responses
// Progress format: moduleOrder + globalStepIndex / 100 (e.g., 6.11)
// ---------------------------------------------------------------------------

/** Returns a copy of the SE (version) with progress-aware nomenclature + process */
function _progressAwareSE(se: any, p: number): any {
  if (!se) return se;
  const copy = { ...se };

  // Nomenclature: empty → 1 line → complete
  // order=7: save at global 11 (7.11), all lines after ajout-done at global 15 (7.15)
  if (copy.nomenclature) {
    if (p >= 7.15) {
      // keep full (after nom-ajout-done reloadAfter)
    } else if (p >= 7.11) {
      copy.nomenclature = { ...copy.nomenclature, nomenclatureLignes: copy.nomenclature.nomenclatureLignes?.slice(0, 1) ?? [] };
    } else {
      copy.nomenclature = { ...copy.nomenclature, nomenclatureLignes: [] };
    }
  }

  // Process: empty → 1 step → complete (with controles)
  if (copy.process?.length) {
    if (p >= 7.11) {
      // Enrich process steps with their pod controles, filtered by availableAt
      copy.process = copy.process.map((pe: any) => {
        if (!pe.podId) return pe;
        const pod = controlePods.find((cp: any) => cp.id === pe.podId);
        if (!pod) return pe;
        const filteredControles = pod.controles
          .filter((c: any) => !c.availableAt || p >= c.availableAt)
          .map((c: any) => _enrichControleIfDone(c, p));
        return filteredControles.length > 0
          ? { ...pe, pod: { ...pod, controles: filteredControles }, controles: filteredControles }
          : pe;
      });
    } else if (p >= 7.10) {
      copy.process = copy.process.slice(0, 1);
    } else {
      copy.process = [];
    }
  }

  return copy;
}

/** Returns a copy of the SEM with progress-aware versions */
function _progressAwareSEM(sem: any, p: number): any {
  if (!sem) return sem;
  return {
    ...sem,
    versions: sem.versions?.map((v: any) => _progressAwareSE(v, p)) ?? [],
  };
}

/** After gestion-production (order 13) is done, controls should appear filled.
 *  Module gestion-traca-production is order 14 → p >= 14 means production controls done. */
function _enrichControleIfDone(c: any, p: number): any {
  if (p < 14) return c;
  const filled: any = { ...c, etat: 'CONFORME', date: _now.toISOString() };
  if (c.type === 'QUANTITATIF') {
    filled.valeur1 = String(c.nominal || 175);
    filled.valeur2 = String(c.nominal || 175);
    filled.moyenne = c.nominal || 175;
  }
  if (c.type === 'CNC') {
    filled.choix = c.correctAnswer || 'C';
  }
  return filled;
}

/** Returns OFs with progression-aware state for OF1 */
function _getOfsWithState(p: number): any[] {
  return ofs
    .filter((o: any) => (!o.availableAt || p >= o.availableAt)
      && (!o.availableUntil || p < o.availableUntil))
    .map((o: any) => {
      if (o.id !== OF_BISCUITS) return o;
      // OF1 state evolves with tutorial progression
      // Module 15 (clôture) starts with EN_PROD — user triggers end-prod during the tuto
      if (p >= 13) return { ...o, etat: 'EN_PROD', started: true, quantiteProduite: p >= 15 ? 250 : 0 };
      return o;
    });
}

const MOCK_ROUTES: MockRouteEntry[] = [
  // --- tutorials/progress (the tutorial system itself) ---
  { pattern: /\/api\/tutorials\/progress$/,       data: [] },

  // --- Articles ---
  { pattern: /\/api\/articles\/?$/,
    data: (_m: RegExpMatchArray, p: number) => {
      if (p >= 4.20) return articles;
      if (p >= 4.10 || p >= 6.11) return articles.filter(a => a.id !== ART_BISCUITS);
      // No progress: exclude Lait and Biscuits
      return articles.filter(a => a.id !== ART_LAIT && a.id !== ART_BISCUITS);
    },
  },
  { pattern: new RegExp(`/api/articles/id/(${UUID})$`),
    data: (m: RegExpMatchArray) => articles.find(a => a.id === m[1]) ?? null },

  // --- SpecificationEtapeMaster ---
  { pattern: new RegExp(`/api/specification_etape/master/article/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const sem = specificationMasters.find(s => s.articleId === m[1]);
      return sem ? _progressAwareSEM(sem, p) : null;
    }},
  { pattern: new RegExp(`/api/specification_etape/master/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const sem = specificationMasters.find(s => s.id === m[1]);
      return sem ? _progressAwareSEM(sem, p) : null;
    }},
  { pattern: new RegExp(`/api/specification_etape/id/(${UUID})/eager/parents$`),
    data: (m: RegExpMatchArray, p: number) => {
      const sem = specificationMasters.find(s => s.id === m[1]);
      if (!sem) return null;
      // Return a copy with progress-aware nomenclature/process on the active version
      return _progressAwareSEM(sem, p);
    }},
  { pattern: new RegExp(`/api/specification_etape/id/(${UUID})/infos$`),
    data: (m: RegExpMatchArray, p: number) => {
      for (const sem of specificationMasters) {
        const v = sem.versions?.find((v: any) => v.id === m[1]);
        if (v) {
          return _progressAwareSE(v, p);
        }
      }
      return null;
    }},
  { pattern: /\/api\/specification_etape\/master\b/,
    data: (_m: RegExpMatchArray, p: number) => {
      if (p >= 4.20) return specificationMasters;
      if (p >= 4.10 || p >= 6.11) return specificationMasters.filter(s => s.articleId !== ART_BISCUITS);
      // No progress: exclude Lait and Biscuits
      return specificationMasters.filter(s => s.articleId !== ART_LAIT && s.articleId !== ART_BISCUITS);
    },
  },

  // --- ArticleInfos ---
  { pattern: /\/api\/specification_etape\/infosArticle\/?$/,
    data: (_m: RegExpMatchArray, p: number) => {
      if (p >= 4.20) return articleInfosList;
      if (p >= 4.10 || p >= 6.11) return articleInfosList.filter(ai => ai.articleId !== ART_BISCUITS);
      return articleInfosList.filter(ai => ai.articleId !== ART_LAIT && ai.articleId !== ART_BISCUITS);
    },
  },
  { pattern: new RegExp(`/api/specification_etape/infosArticle/articleId/(${UUID})$`),
    data: (m: RegExpMatchArray) => articleInfosList.find(ai => ai.articleId === m[1]) ?? null },

  // --- Tiers ---
  { pattern: /\/api\/tiers\/?$/,                  data: tiers },
  { pattern: new RegExp(`/api/tiers/(${UUID})$`),
    data: (m: RegExpMatchArray) => tiers.find(t => t.id === m[1]) ?? null },

  // --- Adresses ---
  { pattern: /\/api\/adresse\/?$/,                data: adresses },
  { pattern: new RegExp(`/api/adresse/id/(${UUID})$`),
    data: (m: RegExpMatchArray) => adresses.find(a => a.id === m[1]) ?? null },
  { pattern: new RegExp(`/api/adresse/(${UUID})$`),
    data: (m: RegExpMatchArray) => adresses.filter(a => a.tiersId === m[1]) },

  // --- Coûts & impact cascade (tuto evolution-couts) ---
  { pattern: new RegExp(`/api/cost/lotId/(${UUID})`), data: () => _buildTutoCostHistory() },
  { pattern: new RegExp(`/api/calcul_cascade/lot/(${UUID})/impact`), data: () => _buildTutoCascadeImpact() },

  // --- Tableau de bord (tuto tableau-de-bord) ---
  { pattern: /\/api\/dashboard\/(global\/)?type\/[A-Z_]+/, data: () => _buildTutoDashboard() },
  { pattern: new RegExp('/api/widget/type/([A-Z_]+)'), data: (m: RegExpMatchArray) => _buildTutoWidget(m[1]) },

  // --- Inventaire (tuto gestion-inventaire) ---
  { pattern: new RegExp(`/api/inventaire/id/(${UUID})$`), data: () => _buildTutoInventaire() },
  { pattern: /\/api\/inventaire\/?$/, data: () => [_buildTutoInventaire()] },
  { pattern: /\/api\/comptage\/search\b/, data: [] },
  { pattern: /\/api\/inventaireBilan\/search\b/, data: [] },

  // --- Transferts entrepôt (tuto gestion-transferts) ---
  { pattern: /\/api\/transferts-entrepot\/navfilter\b/, data: () => [_buildTutoTransfert('EN_REDACTION')] },
  { pattern: new RegExp(`/api/transferts-entrepot/eager/(${UUID})$`), data: () => _buildTutoTransfert('EN_REDACTION') },
  { pattern: new RegExp(`/api/transferts-entrepot/ligne/transfertId/(${UUID})$`), data: () => _buildTutoTransfert('EN_REDACTION').lignes },
  { pattern: /\/api\/transferts-entrepot\/?$/, data: () => [_buildTutoTransfert('EN_REDACTION')] },

  // --- Lots ---
  { pattern: new RegExp(`/api/lots/article/(${UUID})/light_stock$`),
    // quantite en Number : lot.quantite est un String (modèle back) et le
    // sélecteur de stock additionne les quantités ("0" + "250" = "0250")
    data: (m: RegExpMatchArray) => lots.filter(l => l.articleId === m[1]).map(l => ({
      ...l,
      stocks: [
        { id: 'stk-pdc-' + l.id, lotId: l.id, quantite: Number(l.quantite), adresseId: 'adr-pdc-001', adresse: { id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits' } },
        { id: 'stk-ligne-' + l.id, lotId: l.id, quantite: Number(l.quantite), adresseId: ADR_ENTREPOT, adresse: adresses[0] },
        // Stock au siège (adresse de la Ligne Biscuits) : le sélecteur de lot
        // du changement de lot filtre STRICTEMENT par l'adresse de la ligne —
        // sans stock ici, « Aucun stock disponible » dans le tuto traça.
        { id: 'stk-siege-' + l.id, lotId: l.id, quantite: Number(l.quantite), adresseId: ADR_MOULIN, adresse: adresses[1] },
      ],
    })),
  },
  { pattern: new RegExp(`/api/lots/article/(${UUID})/light$`),
    data: (m: RegExpMatchArray) => lots.filter(l => l.articleId === m[1]) },
  { pattern: new RegExp(`/api/lots/article/(${UUID})$`),
    data: (m: RegExpMatchArray) => lots.filter(l => l.articleId === m[1]) },
  { pattern: new RegExp(`/api/lots/id/(${UUID})$`),
    data: (m: RegExpMatchArray) => lots.find(l => l.id === m[1]) ?? null },

  // --- Commandes fournisseur ---
  { pattern: /\/api\/commandes\/?$/,              data: (_m: RegExpMatchArray, p: number) => commandesFournisseur.filter((c: any) => !c.availableAt || p >= c.availableAt) },
  { pattern: /\/api\/commandes\/navfilter\b/,     data: (_m: RegExpMatchArray, p: number) => commandesFournisseur.filter((c: any) => !c.availableAt || p >= c.availableAt) },
  { pattern: new RegExp(`/api/commandes/eager/(${UUID})$`),
    data: (m: RegExpMatchArray) => commandesFournisseur.find(c => c.id === m[1]) ?? null },

  // --- Commandes client ---
  { pattern: /\/api\/commandes_client\/?$/,       data: (_m: RegExpMatchArray, p: number) =>
    commandesClient.map(cc => ({ ...cc, lignes: (cc.lignes ?? []).filter((l: any) => !l.availableAt || p >= l.availableAt) })) },
  { pattern: /\/api\/commandes_client\/navfilter\b/, data: (_m: RegExpMatchArray, p: number) =>
    commandesClient.map(cc => ({ ...cc, lignes: (cc.lignes ?? []).filter((l: any) => !l.availableAt || p >= l.availableAt) })) },
  { pattern: new RegExp(`/api/commandes_client/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const cc = commandesClient.find(c => c.id === m[1]);
      if (!cc) return null;
      return { ...cc, lignes: (cc.lignes ?? []).filter((l: any) => !l.availableAt || p >= l.availableAt) };
    } },
  { pattern: new RegExp(`/api/commandes_client/eager/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const cc = commandesClient.find(c => c.id === m[1]);
      if (!cc) return null;
      return { ...cc, lignes: (cc.lignes ?? []).filter((l: any) => !l.availableAt || p >= l.availableAt) };
    } },

  // --- Custom enums ---
  { pattern: /\/api\/custom_enum\/?$/,            data: (_m: RegExpMatchArray, p: number) => customEnums.filter((e: any) => !e.availableAt || p >= e.availableAt) },
  { pattern: /\/api\/custom_enum\/(FAMILLE_ARTICLE|CATEGORIE_ARTICLE|SOUS_FAMILLE_ARTICLE|FAMILLE_TECHNIQUE|UNITE|UNITE_PROD|EMPLACEMENT_STOCK|POSTE|TITRE|TRAVAIL|TYPE_REGLEMENT|NC_NATURE)$/,
    data: (m: RegExpMatchArray, p: number) => customEnums.filter((e: any) => e.type === m[1] && (!e.availableAt || p >= e.availableAt)) },

  // --- Commande lignes ---
  { pattern: new RegExp(`/api/commandes/ligne/commandeId/(${UUID})$`),
    data: (m: RegExpMatchArray) => {
      const cf = commandesFournisseur.find(c => c.id === m[1]);
      return cf?.lignes ?? [];
    },
  },

  // --- Commande client lignes (back uses commandeId, not commandeClientId) ---
  { pattern: new RegExp(`/api/commandes_client/ligne/commande(?:Client)?Id/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const cc = commandesClient.find(c => c.id === m[1]);
      return (cc?.lignes ?? []).filter((l: any) => !l.availableAt || p >= l.availableAt);
    },
  },

  // --- Pré-préparation / Préparation commande ---
  { pattern: /\/api\/prepreparation\/breakdown\/commande\//, data: [] },
  { pattern: /\/api\/prepreparation\/commande\//, data: [] },
  { pattern: /\/api\/prepreparation\b/, data: [] },

  // --- Unités d'expédition (types) ---
  { pattern: /\/api\/expedition_unit_type\b/, data: (_m: RegExpMatchArray, p: number) => {
    const ue = {
      id: UET_001, denomination: 'Palette Europe', tare: 25, valeur: null,
      articleId: null, articleInfos: null,
      icon: 'inventory_2', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      isActive: true, version: 1, availableAt: 18.06,
    };
    return [ue].filter((e: any) => !e.availableAt || p >= e.availableAt);
  } },

  // --- Expéditions ---
  { pattern: /\/api\/expedition\b/, data: [] },
  { pattern: /\/api\/palette\b/, data: (_m: RegExpMatchArray, p: number) => {
    // Tuto préparation-expédition (order 19) : à partir de l'étape
    // « Ajouter le lot » (p >= 19.07) la palette porte le lot de biscuits ;
    // après l'outro (p >= 19.09) elle est déclarée prête.
    const palette = _buildTutoPalette(p >= 19.07 ? [{ quantite: 100 }] : []);
    if (p >= 19.09) palette.etat = 'PRETE';
    return [palette].filter((e: any) => !e.availableAt || p >= e.availableAt);
  } },
  { pattern: /\/api\/chargement\b/, data: [] },
  { pattern: /\/api\/chargements\/filter-options\b/, data: { transporteurs: [], entrepots: [] } },
  { pattern: /\/api\/chargements\/planning\b/, data: [] },
  { pattern: /\/api\/chargements\b/, data: [] },

  // --- Bons de livraison ---
  { pattern: /\/api\/bon_livraison\b/, data: [] },
  { pattern: /\/api\/bon_livraison_fournisseur\b/, data: [] },
  // not_billed attend un tableau (bl-facade fait un forEach dessus)
  { pattern: /\/api\/bonLivraisons\/not_billed\b/, data: [] },
  { pattern: /\/api\/bonLivraisons\b/, data: { items: [], total: 0 } },
  // Certificats bio du tiers : le fallback détail (URL finissant par un UUID)
  // renverrait null et ferait planter le forEach du chargement de la fiche.
  { pattern: new RegExp(`/api/tiers/certificat/tiersId/(${UUID})$`), data: [] },
  { pattern: /\/api\/expedition_commande\/(not_billed|search)\b/, data: [] },

  // --- Factures ---
  { pattern: /\/api\/facture\b/, data: [] },
  { pattern: /\/api\/factures\b/, data: { items: [], total: 0 } },
  { pattern: /\/api\/facture_fournisseur\b/, data: [] },
  { pattern: /\/api\/transactions\b/, data: [] },
  { pattern: /\/api\/factures\/kpis\b/, data: { totalEnAttente: 0, totalEchu: 0, nbEnAttente: 0, nbEchu: 0 } },
  { pattern: /\/api\/avoir\b/, data: [] },

  // --- Transferts ---
  { pattern: /\/api\/transfert\b/, data: [] },

  // --- Inventaire ---
  { pattern: /\/api\/inventaire\b/, data: [] },

  // --- Tracabilité ---
  // Graphe riche V2 (vue cards /tracabilite-graph/lot/:id — celle affichée par
  // l'onglet Traçabilité de la fiche lot). Sans mock, le fallback détail
  // renvoie null → « g.edges is not iterable » et grille vide dans le tuto.
  { pattern: new RegExp(`/api/tracabilite-graph/lot/(${UUID})`), data: () => _buildTutoTraceGraphV2() },
  { pattern: /\/api\/tracabilite-graph\/lots\b/, data: () => _buildTutoTraceGraphV2() },

  // Graphe Sankey pour la fiche lot (tuto). Couvre /tracabilite/lot/id/:id et
  // /tracabilite/lot/id/:id/unique (fallback HttpClient quand le stream SSE échoue).
  { pattern: /\/api\/tracabilite\/(lot|of)\//, data: () => _buildTutoTracaGraph() },
  { pattern: /\/api\/tracabilite\b/, data: () => _buildTutoTracaGraph() },
  { pattern: /\/api\/sankey\b/, data: [] },

  // --- Article Fourni ---
  { pattern: new RegExp(`/api/article_fourni/tiers/(${UUID})$`), data: (m: RegExpMatchArray, p: number) => {
    const result: any[] = [];
    // After section 2 (parametrage article achat), return Sucre article fourni for Moulin de Provence
    if (m[1] === TIERS_MOULIN && p >= 15.18) {
      result.push({
        id: 'af-tuto-sucre', forClient: false, reference: 'PROV-SUC-001',
        articleId: ART_SUCRE, tiersId: TIERS_MOULIN, tierGroupId: null,
        uniteGestion: 'sac 25kg', coefficientUniteGestion: 25, tva: 5.5,
        multipleAchat: null, multipleVente: null, taxRateId: null, userId: null,
        article: articles[2], articleInfos: articles[2]?.articleInfos,
        tiers: tiers[0], tierGroup: null,
        infosPrix: [{ id: 'ip-tuto-sucre', isPrixAchat: true, quantiteMin: 1, prix: 18, delaiLivraison: 3, articleFourniId: 'af-tuto-sucre' }],
        isActive: true, version: 1,
      });
    }
    // Boulangerie Martin — Biscuits sablés (tarif vente client)
    if (m[1] === TIERS_BOULANG) {
      result.push({
        id: 'af-tuto-biscuits-client', forClient: true, reference: '',
        articleId: ART_BISCUITS, tiersId: TIERS_BOULANG, tierGroupId: null,
        uniteGestion: 'KG', coefficientUniteGestion: 1, tva: 5.5,
        multipleAchat: null, multipleVente: null, taxRateId: null, userId: null,
        article: articles[5], articleInfos: articles[5]?.articleInfos,
        tiers: tiers[1], tierGroup: null,
        infosPrix: [{ id: 'ip-tuto-biscuits', isPrixAchat: false, quantiteMin: 1, prix: 12.50, delaiLivraison: null, articleFourniId: 'af-tuto-biscuits-client' }],
        isActive: true, version: 1,
      });
    }
    return result;
  }},
  { pattern: new RegExp(`/api/article_fourni/tiers/$`), data: [] },
  { pattern: new RegExp(`/api/article_fourni/article/(${UUID})$`), data: (m: RegExpMatchArray) => {
    const result: any[] = [];
    // Biscuits — return the client AF for Boulangerie Martin
    if (m[1] === ART_BISCUITS) {
      result.push({
        id: 'af-tuto-biscuits-client', forClient: true, reference: '',
        articleId: ART_BISCUITS, tiersId: TIERS_BOULANG, tierGroupId: null,
        uniteGestion: 'KG', coefficientUniteGestion: 1, tva: 5.5,
        multipleAchat: null, multipleVente: null, taxRateId: null, userId: null,
        article: articles[5], articleInfos: articles[5]?.articleInfos,
        tiers: tiers[1], tierGroup: null,
        infosPrix: [{ id: 'ip-tuto-biscuits', isPrixAchat: false, quantiteMin: 1, prix: 12.50, delaiLivraison: null, articleFourniId: 'af-tuto-biscuits-client' }],
        isActive: true, version: 1,
      });
    }
    return result;
  }},
  { pattern: new RegExp(`/api/article_fourni/tier-group/(${UUID})$`), data: [] },

  // --- Remises ---
  { pattern: /\/api\/remise\/search/, data: [] },
  { pattern: new RegExp(`/api/remise/tiers/(${UUID})$`), data: [] },

  // --- Tax Rates ---
  { pattern: /\/api\/tax_rate\b/, data: [] },

  // --- Tiers ---
  { pattern: /\/api\/tiers\/?$/, data: tiers },
  { pattern: /\/api\/tiers\/navfilter\b/, data: tiers },
  { pattern: new RegExp(`/api/tiers/id/(${UUID})$`),
    data: (m: RegExpMatchArray) => tiers.find(t => t.id === m[1]) ?? null },
  { pattern: new RegExp(`/api/tiers/(${UUID})/analytics-summary$`),
    data: () => ({ hasSales: false, hasPurchases: false, hasTransport: false }) },

  // --- Custom enums by type ---
  { pattern: /\/api\/custom_enum\/type\/([A-Z_]+)$/,
    data: (m: RegExpMatchArray, p: number) => customEnums.filter((e: any) => e.type === m[1] && (!e.availableAt || p >= e.availableAt)) },

  // --- Nomenclatures ---
  { pattern: /\/api\/nomenclature\/?$/,             data: nomenclatures },
  { pattern: new RegExp(`/api/nomenclature/(${UUID})$`),
    data: (m: RegExpMatchArray) => nomenclatures.find(n => n.id === m[1]) ?? null },
  { pattern: new RegExp(`/api/specification_etape/nomenclature/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const nom = nomenclatures.find(n => n.specificationEtapeId === m[1]);
      if (!nom) return null;
      if (p >= 7.15) return nom;
      if (p >= 7.11) {
        return { ...nom, nomenclatureLignes: nom.nomenclatureLignes.slice(0, 1) };
      }
      // No progress: empty nomenclature lines
      return { ...nom, nomenclatureLignes: [] };
    },
  },

  // --- Process Etapes ---
  { pattern: /\/api\/specification_etape\/process\/?$/,  data: processEtapes },
  { pattern: new RegExp(`/api/specification_etape/process/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const filtered = processEtapes.filter(pe => pe.specificationEtapeId === m[1]);
      if (filtered.length === 0) return filtered;
      if (p >= 7.11) return filtered;
      if (p >= 7.10) return filtered.slice(0, 1);
      // No progress: empty process
      return [];
    },
  },

  // --- Controles ---
  { pattern: /\/api\/controle\/?$/,
    data: (_m: RegExpMatchArray, p: number) => controles
      .filter((c: any) => !c.availableAt || p >= c.availableAt)
      .map((c: any) => _enrichControleIfDone(c, p)) },
  { pattern: new RegExp(`/api/controle/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const c = controles.find(c => c.id === m[1]);
      return c ? _enrichControleIfDone(c, p) : null;
    } },
  { pattern: /\/api\/controle\/general\b/,           data: [] },

  // --- Lignes de production ---
  { pattern: /\/api\/lignes\/eager\/?$/,             data: (_m: RegExpMatchArray, p: number) => lignes.filter((l: any) => !l.availableAt || p >= l.availableAt) },
  { pattern: /\/api\/lignes\/?$/,                    data: (_m: RegExpMatchArray, p: number) => lignes.filter((l: any) => !l.availableAt || p >= l.availableAt) },
  { pattern: new RegExp(`/api/lignes/id/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const l = lignes.find(l => l.id === m[1]);
      if (!l || (l.availableAt && p < l.availableAt)) return null;
      return l;
    }},

  // --- OF (Ordres de Fabrication) ---
  { pattern: /\/api\/of\/nav\b/,                     data: (_m: RegExpMatchArray, p: number) => _getOfsWithState(p) },
  { pattern: /\/api\/of\/navfilter\b/,               data: (_m: RegExpMatchArray, p: number) => _getOfsWithState(p) },
  { pattern: new RegExp(`/api/of/id/eager/(${UUID})$`),
    data: (m: RegExpMatchArray) => ofs.find(o => o.id === m[1]) ?? null },
  { pattern: /\/api\/of\/for_selection\b/,           data: (_m: RegExpMatchArray, p: number) => ofs.filter((o: any) => !o.availableAt || p >= o.availableAt) },
  { pattern: /\/api\/of\/to\/plannif\b/,             data: (_m: RegExpMatchArray, p: number) => ofs.filter((o: any) => (!o.availableAt || p >= o.availableAt) && o.etat === 'EN_ATTENTE_PLANNIF') },

  // --- Pied de Chaîne (production supply chain) ---
  // Structure alignée sur PiedDeChaineService.buildForOf() côté back
  { pattern: new RegExp(`/api/of/pdc/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const of = ofs.find((o: any) => o.id === m[1]);
      if (!of) return null;
      // articlesLots : 1 entrée par ligne de nomenclature
      const artLots = nomenclatureLignes.map((nl: any) => {
        const art = articles.find((a: any) => a.id === nl.articleId);
        const ai = art?.articleInfos;
        const artLots = lots.filter((l: any) => l.articleId === nl.articleId)
          .map((l: any) => ({
            ...l,
            stocks: [{ id: 'stk-' + l.id, lotId: l.id, quantite: l.quantite, adresseId: 'adr-pdc-001', adresse: { id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits' } }],
          }));
        const semId = art?.specificationEtapeMasterId;
        const pdc = ai?.withPiedDeChaine
          ? artLots.map((l: any) => ({ ...l, stocks: [{ id: 'stk-pdc-' + l.id, lotId: l.id, quantite: l.quantite, adresseId: 'adr-pdc-001', adresse: { id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits' } }] }))
          : [];
        return {
          articleInfos: ai,
          articleId: nl.articleId,
          lots: artLots,
          pdc,
          tiersId: null,
          specificationEtapeRefId: semId ? (semId + '-se') : null,
        };
      });
      return {
        adresse: { id: 'adr-pdc-001', denomination: 'Pied de chaîne - Ligne Biscuits' },
        lot: lots.find((l: any) => l.articleId === ART_BISCUITS),
        articlesLots: artLots,
        nomenclature: { nomenclatureLignes },
        path: {
          id: 'path-biscuits',
          quantityProdDefault: 250,
          processEtapes,
          nomenclatureLignes,
          articleInfos: articleInfosBiscuits,
          podControlesProductionIds: [],
          podControlesCalageIds: [],
          podControlesFinIds: [],
          podControlesStartOpeIds: [],
          podControlesEndOpeIds: [],
          process: [{ process: processEtapes }],
          checklists: [],
        },
        of: (() => {
          // For traca module (progress >= 14), include a poste with all PEOs done
          if (p >= 14) {
            const lotBiscuits = lots.find((l: any) => l.articleId === ART_BISCUITS);
            const now = new Date().toISOString();
            const tutoMouvements = [
              { id: 'mvt-pdc-farine', type: 'PIED_DE_CHAINE', articleId: ART_FARINE, lotId: LOT_FARINE, quantite: 100, date: now, ofId: OF_BISCUITS, lot: lots[0], articleInfos: articleInfosFarine },
              { id: 'mvt-pdc-lait', type: 'PIED_DE_CHAINE', articleId: ART_LAIT, lotId: LOT_LAIT, quantite: 12.5, date: now, ofId: OF_BISCUITS, lot: lots[4], articleInfos: articleInfosLait },
            ];
            // OF stays EN_PROD — user triggers end-prod during cloture tuto
            const isCloture = p >= 15;
            const ofEtat = 'EN_PROD';
            const poste = isCloture ? _buildTutoPoste('TERMINEE') : _buildTutoPoste();
            return { ...of, etat: ofEtat, started: true, withQuantiteOpe: true, quantiteProduite: isCloture ? 250 : 0, qttReinteTemp: isCloture ? 250 : 0, postes: [poste], integrations: [], bilans: [], lots: lotBiscuits ? [lotBiscuits] : [], mouvements: tutoMouvements };
          }
          return { ...of, postes: of.postes ?? [], integrations: of.integrations ?? [], bilans: of.bilans ?? [] };
        })(),
      };
    },
  },

  // --- Travail (Hours) ---
  { pattern: new RegExp(`/api/travail/of/(${UUID})$`), data: (_m: RegExpMatchArray, p: number) => p >= 15 ? _buildTutoTravaux() : [] },

  // --- Poste (Operations) ---
  { pattern: /\/api\/poste\/?$/, data: [] },
  { pattern: new RegExp(`/api/poste/of/(${UUID})$`), data: [] },
  // poste/eager — returns the tuto poste when progress >= 14 (traca module)
  { pattern: new RegExp(`/api/poste/eager/(${UUID})$`), data: (_m: RegExpMatchArray, p: number) => {
    if (p < 14) return null;
    const poste = _buildTutoPoste(p >= 15 ? 'TERMINEE' : undefined);
    // Deep-enrich: mark spec controles inside process steps as CONFORME
    // so the UI (which reads spec controles, not PEO controles) shows them filled.
    if (p >= 14 && poste.processesEtapeDone) {
      for (const peo of poste.processesEtapeDone) {
        const pe = peo.processEtape;
        if (pe?.controles) {
          pe.controles = pe.controles.map((c: any) => _enrichControleIfDone(c, p));
        }
        if (pe?.pod?.controles) {
          pe.pod.controles = pe.pod.controles.map((c: any) => _enrichControleIfDone(c, p));
        }
      }
    }
    return poste;
  }},
  { pattern: new RegExp(`/api/poste/changements/(${UUID})$`), data: (_m: RegExpMatchArray, p: number) => {
    if (p >= 14) return _buildTutoChangementsLot();
    return [];
  }},
  { pattern: new RegExp(`/api/poste/changements/posteId/(${UUID})$`), data: (_m: RegExpMatchArray, p: number) => {
    if (p >= 14) return _buildTutoChangementsLot();
    return [];
  }},

  // --- Tempo (intervals de contrôle) ---
  { pattern: new RegExp(`/api/tempo/bundles/posteIntervals/posteId/(${UUID})$`), data: [] },

  // --- PEO (ProcessEtapeOpe) ---
  { pattern: new RegExp(`/api/peo/id/(${UUID})$`), data: () => null },
  { pattern: /\/api\/peo\/search\b/, data: (_m: RegExpMatchArray, p: number) => {
    if (p >= 14) return _buildTutoPeos();
    return [];
  }},

  // --- Controles ---
  { pattern: new RegExp(`/api/controle/podId/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const pod = controlePods.find((cp: any) => cp.id === m[1]);
      if (!pod) return [];
      return pod.controles
        .filter((c: any) => !c.availableAt || p >= c.availableAt)
        .map((c: any) => _enrichControleIfDone(c, p));
    },
  },

  // --- Lots par article ---
  { pattern: new RegExp(`/api/lots/by_article/(${UUID})$`),
    data: (m: RegExpMatchArray) => lots.filter((l: any) => l.articleId === m[1]),
  },

  // --- Adresses ---
  { pattern: /\/api\/adresse\b/, data: [{ id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits', isActive: true }] },

  // --- Checklists ---
  { pattern: new RegExp(`/api/checklist/for_of/(${UUID})$`), data: [] },

  // --- Mouvements ---
  // --- Mouvements d'un lot (fiche lot, onglet Mouvements — tuto traçabilité).
  // Cycle plausible : fabrication sur OF, transfert entrepôt → siège, puis
  // expédition client d'une partie du lot.
  { pattern: new RegExp(`/api/lot_mouvements/lotId/(${UUID})$`),
    data: (m: RegExpMatchArray) => {
      const lot = lots.find((l: any) => l.id === m[1]);
      if (!lot) return [];
      const qty = Number(lot.quantite) || 100;
      const base = {
        lotId: lot.id, lot, stockId: null, stockDepartId: null,
        emplacementDepart: null, emplacementArrive: null,
        emplacementDepartId: null, emplacementArriveId: null,
        commandeId: null, commandeNumero: null, transfertEntrepot: null,
        transfertEntrepotId: null, sscc: null, paletteId: null, tiersId: null,
        coutUnitaireMatiere: lot.coutMaterielUnitaire, commentaire: '', isActive: true,
      };
      return [
        { ...base, id: 'mvt-' + lot.id + '-1', groupId: 'g1', type: 'FABRICATION', quantite: qty,
          date: _workDay(-3, 14), ofId: OF_BISCUITS, ofNumero: 'OF2600001',
          adresseDepart: null, adresseDepartId: null, adresseArrive: adresses[0], adresseArriveId: ADR_ENTREPOT,
          commandeClient: null, commandeClientId: null },
        { ...base, id: 'mvt-' + lot.id + '-2', groupId: 'g2', type: 'TRANSFERT', quantite: qty * 0.2,
          date: _workDay(-2, 9), ofId: null, ofNumero: null,
          adresseDepart: adresses[0], adresseDepartId: ADR_ENTREPOT, adresseArrive: adresses[1], adresseArriveId: ADR_MOULIN,
          commandeClient: null, commandeClientId: null },
        { ...base, id: 'mvt-' + lot.id + '-3', groupId: 'g3', type: 'EXPEDITION', quantite: qty * 0.4,
          date: _workDay(-1, 16), ofId: null, ofNumero: null,
          adresseDepart: adresses[0], adresseDepartId: ADR_ENTREPOT, adresseArrive: null, adresseArriveId: null,
          commandeClient: commandesClient?.[0] ?? null, commandeClientId: CC_001 },
      ];
    },
  },

  { pattern: /\/api\/lot_mouvements\b/, data: (_m: RegExpMatchArray, p: number) => {
    if (p >= 14) {
      const now = new Date().toISOString();
      return [
        { id: 'mvt-pdc-farine', type: 'PIED_DE_CHAINE', articleId: ART_FARINE, lotId: LOT_FARINE, quantite: 100, date: now, ofId: OF_BISCUITS, lot: lots[0], articleInfos: articleInfosFarine },
        { id: 'mvt-pdc-lait', type: 'PIED_DE_CHAINE', articleId: ART_LAIT, lotId: LOT_LAIT, quantite: 0.05, date: now, ofId: OF_BISCUITS, lot: lots[4], articleInfos: articleInfosLait },
      ];
    }
    return [];
  }},

  // --- Integration PF ---
  { pattern: /\/api\/integrations\b/, data: [] },

  // --- Stock outdated ---
  { pattern: new RegExp(`/api/of/stock_outdated/(${UUID})$`), data: [] },

  // --- Production Navigation ---
  { pattern: /\/api\/of\/prod\/fast-nav\b/, data: (_m: RegExpMatchArray, p: number) => {
    return ofs
      .filter((o: any) => (!o.availableAt || p >= o.availableAt) && (o.etat === 'EN_PROD' || o.etat === 'EN_ATTENTE_PROD'))
      .map((o: any) => ({ fo: o, doc: null }));
  }},

  // --- Stock ---
  { pattern: new RegExp(`/api/stock/lot/(${UUID})$`),
    data: (m: RegExpMatchArray) => {
      const lot = lots.find((l: any) => l.id === m[1]);
      if (!lot) return [];
      return [{ id: 'stk-' + lot.id, lotId: lot.id, quantite: lot.quantite, adresseId: 'adr-pdc-001' }];
    },
  },
  // GET /stock (StockForArticle) — géré dans l'interceptor (conditionné au
  // module planification-composants actif), pour ne pas polluer les autres
  // tutos. Ici on renvoie [] par défaut.
  { pattern: /\/api\/planification\/planification-composant-events/, data: () => _buildTutoPlanifEvents() },
  { pattern: /\/api\/stock\b/, data: [] },

  // --- Horaires ---
  { pattern: new RegExp(`/api/horaires/ligne/(${UUID})$`),
    data: () => [{
      id: 'hor-ligne-bisc',
      ligneId: LIGNE_BISC,
      lastChangedDateTime: new Date('2026-01-01').toISOString(),
      lundi: ['09:00-17:00'],
      mardi: ['09:00-17:00'],
      mercredi: ['09:00-17:00'],
      jeudi: ['09:00-17:00'],
      vendredi: ['09:00-17:00'],
      samedi: [],
      dimanche: [],
    }],
  },

  // --- Créneaux (planification Gantt) ---
  { pattern: /\/api\/creneau\b/,
    data: (_m: RegExpMatchArray, p: number) => {
      // Generate creneaux from planned OFs (those with dateDebut + dateFin and visible at current progress)
      return ofs
        .filter((o: any) => o.dateFin && (!o.availableAt || p >= o.availableAt))
        .map((o: any) => ({
          id: 'cr-' + o.id,
          start: o.dateDebut,
          end: o.dateFin,
          ligneId: o.ligneId,
          ofId: o.id,
          ofNumero: o.numero,
          ofArticle: o.articleInfos?.denomination,
          of: o,
          type: 'PLANIFICATION',
          coefPerformance: 1,
          horairesFixes: false,
        }));
    },
  },

  // --- Controles / Pods ---
  { pattern: new RegExp(`/api/controle_pod/id/(${UUID})$`),
    data: (m: RegExpMatchArray, p: number) => {
      const pod = controlePods.find((cp: any) => cp.id === m[1]);
      if (!pod) return null;
      return { ...pod, controles: pod.controles
        .filter((c: any) => !c.availableAt || p >= c.availableAt)
        .map((c: any) => _enrichControleIfDone(c, p))
      };
    },
  },
  { pattern: /\/api\/controle\/general\b/,          data: [] },

  // --- Specif Modifs (demandes de modification) ---
  { pattern: /\/api\/specif_modifs\/semId\//,       data: [] },

  // --- Specification Variable ---
  // Variables d'un MODÈLE (onglet "Paramètres de spécification" d'un modèle).
  { pattern: new RegExp(`/api/specification-variable/model/(${UUID})/(${UUID})$`),
    data: (m: RegExpMatchArray) => (m[1] === SEM_MODEL ? MODEL_VARIABLES : []) },
  // Variables d'un ARTICLE (siennes renseignées + héritées du modèle).
  { pattern: new RegExp(`/api/specification-variable/article/(${UUID})/(${UUID})$`),
    data: (m: RegExpMatchArray) => (m[2] === SE_BISCUITS ? ARTICLE_VARIABLES_BISCUITS : []) },
  { pattern: /\/api\/specification-variable\//,     data: [] },

  // --- Listes de tâches (tour premiers-pas : la page /lists doit montrer le
  // planning du jour des opérateurs, pas « Aucun élément ») ---
  { pattern: /\/api\/ws\/list\/?$/, data: () => [
    { id: 'list-jour-0000-4000-8000-000000000001', denomination: 'Atelier — production du jour', type: 'JOUR', ligneId: LIGNE_BISC, ligneIds: [LIGNE_BISC], users: '', entityLinks: [] },
    { id: 'list-mois-0000-4000-8000-000000000002', denomination: 'Plan de production — juillet', type: 'MOIS', ligneId: LIGNE_BISC, ligneIds: [LIGNE_BISC], users: '', entityLinks: [] },
  ] },

  // --- Pays (référentiel — nécessaire au formulaire d'adresse du tuto
  // paramétrage : sans lui, le select Pays est vide et l'enregistrement
  // échoue en erreur de formulaire) ---
  { pattern: /\/api\/pays\b/, data: [
    { id: 'pays-fr-0000-4000-8000-000000000001', name: 'France', isoCode: 'FR', zoneGeoId: 'zg-ue-0000-4000-8000-000000000001', isActive: true },
    { id: 'pays-be-0000-4000-8000-000000000002', name: 'Belgique', isoCode: 'BE', zoneGeoId: 'zg-ue-0000-4000-8000-000000000001', isActive: true },
    { id: 'pays-es-0000-4000-8000-000000000003', name: 'Espagne', isoCode: 'ES', zoneGeoId: 'zg-ue-0000-4000-8000-000000000001', isActive: true },
  ] },

  // --- Activity Infos (entreprise) ---
  { pattern: /\/api\/activity_infos\/?$/,           data: activityInfos },

  // --- Path model ---
  { pattern: /\/api\/specification_etape\/path\//,  data: [] },
  // Articles liés à un modèle (dialog "Articles liés")
  { pattern: new RegExp(`/api/specification_etape/models/(${UUID})/sems$`),
    data: (m: RegExpMatchArray) => (m[1] === SEM_MODEL ? [specificationMasters[5]] : []) },
  // Liste des modèles pour le sélecteur d'association + page liste
  { pattern: /\/api\/specification_etape\/models/,  data: SPECIFICATION_MODELS_FOR_SELECTION },

  // --- Catch-all for write operations in demo mode (return empty success) ---
  // POST/PUT/DELETE endpoints get an empty 200 from the interceptor itself;
  // this file only provides GET data.
];

/**
 * Looks up mock data for a given URL.
 * @param url The request URL to match.
 * @param progress Optional progress value for progressive data.
 * @returns the mock response body, or `null` if no mock is registered.
 */
export function getDemoResponse(url: string, progress?: number): unknown | null {
  // Strip query string for matching
  const pathname = url.split('?')[0];
  const p = progress ?? 0;

  for (const route of MOCK_ROUTES) {
    const match = pathname.match(route.pattern);
    if (match) {
      return typeof route.data === 'function' ? route.data(match, p) : route.data;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tuto préparation-expédition — palette + builder d'expédition
// ---------------------------------------------------------------------------

const TUTO_PALETTE_SSCC = '003412345678901234';

/** Stock « lot de biscuits posé sur la palette » (un par mouvement enregistré). */
function _tutoPaletteStocks(mouvements: any[]): any[] {
  return mouvements.map((m: any, i: number) => ({
    id: 'stk-pal-biscuits-' + i,
    lotId: LOT_BISCUITS,
    articleId: ART_BISCUITS,
    quantite: m?.quantite ?? 100,
    adresseId: ADR_ENTREPOT,
    adresse: adresses[0],
    paletteId: PAL_001,
    paletteSSCC: TUTO_PALETTE_SSCC,
    commandeClientId: CC_001,
    commandeClientLigneId: CCL_001,
    lot: lots[5],
    lotNumero: lots[5].numero,
    lotDluo: lots[5].dluo,
    emplacementId: null,
  }));
}

/**
 * Palette du tuto (CC Boulangerie Martin). `mouvements` = lots ajoutés via
 * « Préparer » : ils deviennent des stocks portés par la palette.
 */
export function _buildTutoPalette(mouvements: any[] = []): any {
  return {
    id: PAL_001,
    denomination: 'Palette Europe 1 / Boulangerie Martin / CC2600001',
    sscc: TUTO_PALETTE_SSCC,
    etat: 'EN_PREPARATION',
    isActive: true,
    stocks: _tutoPaletteStocks(mouvements),
    expeditions: [], expeditionsTransfert: [],
    commandesClientsIds: [CC_001], transfertsEntrepotIds: [],
    adresseId: ADR_ENTREPOT, entrepotSourceId: ADR_ENTREPOT,
    expeditionUnitTypeId: UET_001,
    expeditionUnitType: {
      id: UET_001, denomination: 'Palette Europe', tare: 25,
      icon: 'inventory_2', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    bonLivraisonId: null, bonLivraison: null,
    bonTransfertId: null, bonTransfert: null,
    dateExpedition: null, selected: false,
    customTare: 25, customName: null, podId: null,
    statusHistory: null, linkId: null,
    availableAt: 18.21,
  };
}

/**
 * Breakdown de pré-préparation de la CC du tuto
 * (GET /prepreparation/breakdown/commande/:id — sert le badge x/y KG de la
 * page préparation). `enPalette` suit les mouvements posés sur la palette ;
 * fallback sur la progression (p >= 19.21 = après prep-ue-confirm) si l'état
 * interceptor est perdu (reload de page en cours de tuto).
 *
 * Continuité avec la section grille du module : à partir de p >= 19.13
 * (« Réserver toutes » confirmé), les 100 KG de la ligne sont réservés en
 * zone prépa (`engage`), puis basculent vers `enPalette` au fil des
 * mouvements de palettisation.
 */
export function _buildTutoBreakdown(commandeClientId: string, mouvements: any[] = [], p = 0, gridEngaged = false): any[] {
  if (commandeClientId !== CC_001) return [];
  const moved = mouvements.reduce((sum: number, m: any) => sum + (m?.quantite ?? 0), 0);
  const enPalette = moved > 0 ? moved : (p >= 19.21 ? 100 : 0);
  const engaged = gridEngaged || p >= 19.13;
  const engage = engaged ? Math.max(0, 100 - enPalette) : 0;
  return [{
    ligneId: CCL_001,
    articleId: ART_BISCUITS,
    articleDenomination: 'Biscuits sables du Moulin',
    refClient: '',
    uniteArticle: 'KG',
    typeDateLimite: 'DDM',
    quantite: 100,
    pasDemarre: Math.max(0, 100 - enPalette - engage),
    enSimulation: 0,
    engage,
    enPalette,
    expedie: 0,
    ddmMini: { jours: TUTO_DDM_CC1, niveauSource: 'article', niveauLabel: 'Article' },
  }];
}

/**
 * Builder d'expédition pour la CC du tuto (POST /expedition_commande/build).
 *
 * - `expe` : stocks à l'adresse de départ — les mouvements enregistrés y
 *   apparaissent avec `paletteSSCC` → la ligne passe en « sur palette ».
 * - `lots` : stock restant du lot sur un autre site → `stockDisponible` > 0,
 *   pas de faux « Rupture de stock » pendant le tuto.
 */
export function _buildTutoExpeditionBuilder(commande: any, mouvements: any[] = []): any {
  const ccl = commande?.lignes?.find((l: any) => l.articleId === ART_BISCUITS)
    ?? (commandesClient[0].lignes[0] as any);
  const lotBiscuits = lots[5];
  const movedQty = mouvements.reduce((sum: number, m: any) => sum + (m?.quantite ?? 0), 0);

  return {
    articlesLots: [{
      articleId: ART_BISCUITS,
      commandeClientLigneId: ccl?.id ?? CCL_001,
      lots: [{
        ...lotBiscuits,
        stocks: [{
          id: 'stk-build-reserve',
          lotId: LOT_BISCUITS,
          quantite: Math.max(0, Number(lotBiscuits.quantite) - movedQty),
          adresseId: ADR_MOULIN,
          adresse: adresses[1],
        }],
      }],
      expe: mouvements.length > 0 ? [{
        ...lotBiscuits,
        stocks: _tutoPaletteStocks(mouvements),
      }] : [],
      gone: [],
      valid: false,
    }],
    adresse: { id: ADR_ENTREPOT, denomination: 'Entrepôt principal' },
    commande: commande ?? commandesClient[0],
  };
}

// ---------------------------------------------------------------------------
// Tuto préparation de commande — pilotage par produit + grille DDM (module 19)
//
// Scénario : 3 lots de biscuits à DLUO échelonnées (FEFO) × 2 commandes aux
// contraintes DDM différentes (Boulangerie Martin 30 j, FraisMalin 90 j).
// La « Pré-réservation Auto » illustre le juste-assez : le lot 6 mois sert
// Martin et complète FraisMalin, le lot 12 mois couvre le reste, le lot
// court (20 j) n'est éligible pour personne (statut FORCE, masqué par défaut).
//
// État piloté par l'interceptor (_gridPrereservee / _gridEngagee) avec
// fallback progression (reload en cours de tuto) :
//   p >= 19.09 → pré-réservé (après le step prepa-grille-auto)
//   p >= 19.13 → réservé physiquement (après prepa-grille-reserver-confirm)
// ---------------------------------------------------------------------------

const LOT_BIS_COURT = 'dddddd01-aaaa-4bbb-cccc-dddddddddddd';
const LOT_BIS_LONG  = 'dddddd02-aaaa-4bbb-cccc-dddddddddddd';
const _inTwentyDays = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + 20).toISOString();

const _dayIso = (iso: string): string => iso.slice(0, 10);
const _daysFromNow = (iso: string): number =>
  Math.round((new Date(iso).getTime() - _now.getTime()) / 86_400_000);

/** Contraintes DDM mini contractuelles des 2 CC du tuto. */
const TUTO_DDM_CC1 = 30; // Boulangerie Martin (source article)
const TUTO_DDM_CC2 = 90; // Supermarché FraisMalin (source client)

/** Lots de biscuits de la grille, triés DLUO ASC côté front (FEFO). */
function _tutoGridLots(): any[] {
  const entrepots = [{ id: ADR_ENTREPOT, denomination: adresses[0]?.denomination ?? 'Entrepôt principal' }];
  const mk = (id: string, numero: string, dluo: string, qty: number) => ({
    id, numero, dluo: _dayIso(dluo), typeDateLimite: 'DDM', dateCreation: _fiveDaysAgo,
    stockPhysique: qty, stockReserveAutreFlux: 0, stockDispoTotal: qty,
    entrepotIds: [ADR_ENTREPOT], entrepots,
    stockParEntrepot: [{ entrepotId: ADR_ENTREPOT, qty }],
    stockLibreParEntrepot: [{ entrepotId: ADR_ENTREPOT, qty }],
  });
  return [
    mk(LOT_BIS_COURT, 'BIS-2025-104', _inTwentyDays, 80),
    mk(LOT_BISCUITS, 'BIS-2026-001', _inSixMonths, 250),
    mk(LOT_BIS_LONG, 'BIS-2026-002', _inTwelveMonths, 200),
  ];
}

/** Cellules FORCE du lot court : DDM insuffisante pour les 2 commandes. */
function _tutoGridForceCells(): any[] {
  const dCourt = _daysFromNow(_inTwentyDays);
  const raison = 'DDM insuffisante : le lot expire trop tôt par rapport à la DDM mini contractuelle de la commande.';
  return [
    { ligneId: CCL_001, lotId: LOT_BIS_COURT, qtyAffectee: 0, statut: 'FORCE', marge: dCourt - _daysFromNow(_inTwoDays) - TUTO_DDM_CC1, raisonNonEligible: raison },
    { ligneId: CCL_003, lotId: LOT_BIS_COURT, qtyAffectee: 0, statut: 'FORCE', marge: dCourt - _daysFromNow(_inFourDays) - TUTO_DDM_CC2, raisonNonEligible: raison },
  ];
}

/** Affectations produites par la « Pré-réservation Auto » (juste-assez). */
function _tutoGridAutoCells(engaged: boolean): any[] {
  const dSix = _daysFromNow(_inSixMonths);
  const dTwelve = _daysFromNow(_inTwelveMonths);
  return [
    { ligneId: CCL_001, lotId: LOT_BISCUITS, qtyAffectee: 100, statut: 'OK', marge: dSix - _daysFromNow(_inTwoDays) - TUTO_DDM_CC1, engagee: engaged },
    { ligneId: CCL_003, lotId: LOT_BISCUITS, qtyAffectee: 150, statut: 'OK', marge: dSix - _daysFromNow(_inFourDays) - TUTO_DDM_CC2, engagee: engaged },
    { ligneId: CCL_003, lotId: LOT_BIS_LONG, qtyAffectee: 100, statut: 'OK', marge: dTwelve - _daysFromNow(_inFourDays) - TUTO_DDM_CC2, engagee: engaged },
  ];
}

/**
 * Grille pilotage par produit du tuto
 * (GET /prepreparation/pilotage/by-produit/:articleId/grid).
 */
export function _buildTutoProduitGrid(prereserved: boolean, engaged: boolean): any {
  const art = articles[5];
  const commandes = [
    {
      ligneId: CCL_001, ccId: CC_001, ccNumero: 'CC2600001',
      tiersDenomination: tiers[1]?.denomination ?? 'Boulangerie Martin',
      besoinUV: 100, besoinUG: 100, uniteVente: null, coefficient: 1,
      entrepotsAssignes: [],
      dateExpe: _dayIso(_inTwoDays), dateLivraison: _dayIso(_inTwoDays),
      dateExpeEffective: _dayIso(_inTwoDays), dateLivraisonEffective: _dayIso(_inTwoDays),
      livraisonEnRetard: false, joursRetard: 0,
      ddmMini: { jours: TUTO_DDM_CC1, niveauSource: 'article' },
      etatPrep: engaged ? 'engagee' : (prereserved ? 'simulation' : 'aucun'),
      verrouillee: engaged,
    },
    {
      ligneId: CCL_003, ccId: CC_002, ccNumero: 'CC2600002',
      tiersDenomination: tiers[2]?.denomination ?? 'Supermarché FraisMalin',
      besoinUV: 250, besoinUG: 250, uniteVente: null, coefficient: 1,
      entrepotsAssignes: [],
      dateExpe: _dayIso(_inFourDays), dateLivraison: _dayIso(_inFourDays),
      dateExpeEffective: _dayIso(_inFourDays), dateLivraisonEffective: _dayIso(_inFourDays),
      livraisonEnRetard: false, joursRetard: 0,
      ddmMini: { jours: TUTO_DDM_CC2, niveauSource: 'tiers' },
      etatPrep: engaged ? 'engagee' : (prereserved ? 'simulation' : 'aucun'),
      verrouillee: engaged,
    },
  ];
  const cells = [
    ..._tutoGridForceCells(),
    ...(prereserved || engaged ? _tutoGridAutoCells(engaged) : []),
  ];
  return {
    article: {
      id: ART_BISCUITS,
      denomination: art?.denomination ?? 'Biscuits sablés du Moulin',
      code: (art as any)?.code ?? 'PF-BIS-001',
      uniteArticle: 'KG',
      typeDateLimite: 'DDM',
      withLot: true,
      isPrestation: false,
    },
    nextOf: null,
    lots: _tutoGridLots(),
    commandes,
    cells,
    stats: {
      besoinTotalUG: 350, besoinTotalUV: 350,
      stockDispoTotal: 530, stockDispoEligible: 450, manquant: 0,
      qtyEnOF: 0, nbCommandes: 2, nbLignes: 2,
      coefficient: 1, uniteVente: null,
      premierDepart: _dayIso(_inTwoDays),
    },
  };
}

/**
 * Cards de la page pilotage (GET /prepreparation/pilotage/by-produit).
 */
export function _buildTutoPilotageProduitRows(prereserved: boolean, engaged: boolean): any[] {
  const art = articles[5];
  return [{
    articleId: ART_BISCUITS,
    articleDenomination: art?.denomination ?? 'Biscuits sablés du Moulin',
    besoinTotal: 350,
    qtyReservee: prereserved || engaged ? 350 : 0,
    qtyEngageePhysique: engaged ? 350 : 0,
    stockPhysique: 530,
    stockDispo: 530,
    stockDispoEligible: engaged ? 100 : 450,
    qtyEnOF: 0,
    couverturePct: prereserved || engaged ? 100 : 0,
    nbCommandes: 2,
    nbLignes: 2,
    nbLignesARevoir: 0,
    premierDeparture: _dayIso(_inTwoDays),
    uniteVente: null,
    uniteArticle: 'KG',
    coefficient: 1,
    ccNumeros: ['CC2600001', 'CC2600002'],
    tiersNames: [tiers[1]?.denomination ?? 'Boulangerie Martin', tiers[2]?.denomination ?? 'Supermarché FraisMalin'],
    tiersGroupNames: [],
    typeDateLimite: 'DDM',
    withLot: true,
    isPrestation: false,
  }];
}

/**
 * Résumé pilotage d'un article — tooltip « infos réf »
 * (GET /prepreparation/pilotage/by-produit/:id/summary).
 * Avant p 17.16 (création de la ligne CC du tuto commandes client), seule la
 * commande FraisMalin (250 KG) existe dans le dataset.
 */
export function _buildTutoArticleSummary(p: number, prereserved: boolean, engaged: boolean): any {
  const withCc1 = p >= 17.16;
  return {
    articleId: ART_BISCUITS,
    uniteArticle: 'KG',
    uniteVente: null,
    coefficient: 1,
    besoinUG: withCc1 ? 350 : 250,
    nbCommandes: withCc1 ? 2 : 1,
    engageUG: engaged ? 350 : (prereserved ? 350 : 0),
    stockDispo: 530,
    stockDispoEligible: engaged ? 100 : 450,
    stockDispoEligibleLigne: engaged ? 100 : 450,
  };
}

// ---------------------------------------------------------------------------
// Tuto livraison — chargement + bon de livraison
// ---------------------------------------------------------------------------

const TUTO_TRANSPORTEUR = {
  id: TIERS_TRANSP, denomination: 'Transports Rapides SARL',
  isTransporteur: true, isClient: false, isFournisseur: false,
  mail: 'contact@transports-rapides.fr', telephone: '04 90 55 66 77',
  isActive: true, version: 1,
};

/** Bon de livraison généré pour la palette prête (CC Boulangerie Martin). */
export function _buildTutoBL(charge = false): any {
  const palette = _buildTutoPalette([{ quantite: 100 }]);
  palette.etat = 'PRETE';
  return {
    id: BL_001,
    numero: 'BL2600001',
    denomination: 'BL2600001 / Boulangerie Martin',
    date: _now.toISOString(),
    tiersId: TIERS_BOULANG, tiers: tiers[1],
    adresseId: ADR_BOULANG, adresse: adresses[1],
    commandesClientsIds: [CC_001], commandesClientsNumeros: ['CC2600001'],
    palettes: [palette],
    expeditions: [], facture: null, factureId: null,
    podId: null, pod: null,
    chargementId: CHARG_001,
    etat: charge ? 'EXPEDIE_NON_FACTURE' : 'PRET',
    isArchived: false,
    palettesCount: 1,
    poidsBrutTotal: 130,
    isActive: true, version: 1,
  };
}

/**
 * Chargement du tuto livraison.
 * @param etat EN_PREPARATION | PRET | CHARGE
 */
export function _buildTutoChargement(etat = 'EN_PREPARATION'): any {
  return {
    id: CHARG_001,
    numero: 'CH2600001',
    date: _now.toISOString(),
    etat,
    bls: [_buildTutoBL(etat === 'CHARGE')],
    bts: [],
    commandesClientsIds: [CC_001],
    transporteur: TUTO_TRANSPORTEUR, transporteurId: TIERS_TRANSP,
    pod: null, podId: null,
    entrepotSource: adresses[0], entrepotSourceId: ADR_ENTREPOT,
    expeditionState: {},
    signatureOperateur: '', signatureChauffeur: '', observations: '',
    lettreVoitureDocListId: null, observationsDocListId: null,
    statusHistory: {},
    palettes: [_buildTutoPalette([{ quantite: 100 }])],
    isActive: true, version: 1,
  };
}

/** DTO léger pour GET /chargements/planning. */
export function _buildTutoChargementPlanning(etat = 'EN_PREPARATION'): any[] {
  return [{
    id: CHARG_001,
    numero: 'CH2600001',
    date: _now.toISOString(),
    etat,
    transporteurDenomination: 'Transports Rapides SARL',
    entrepotSourceDenomination: 'Entrepôt principal',
    nbBls: 1,
    nbPalettes: 1,
  }];
}

// ---------------------------------------------------------------------------
// Tuto facturation — facture créée depuis le bon de livraison
// ---------------------------------------------------------------------------

const FACT_001 = 'fact0001-aaaa-4bbb-cccc-dddddddddddd';

/**
 * Facture du tuto facturation. Créée depuis le BL (POST /factures/bl/create),
 * EN_REDACTION, regroupe la ligne biscuits (100 KG × 12,50 €).
 */
export function _buildTutoFacture(p = 0): any {
  const ht = 1250;
  const tva = +(ht * 0.055).toFixed(2);
  // État progressif : la facture passe EMISE une fois le step « figer »
  // dépassé (module 21, idx global 12) — sinon le bouton « Envoyer »
  // (masqué en rédaction) n'apparaît jamais au step fact-send.
  const etat = p >= 21.12 ? 'EMISE' : 'EN_REDACTION';
  return {
    id: FACT_001,
    numero: 'FC2600001',
    denomination: 'FC2600001 / Boulangerie Martin',
    date: _now.toISOString(), dateString: _now.toISOString().slice(0, 10),
    dateEcheance: _inSixMonths, dateEcheanceString: String(_inSixMonths).slice(0, 10),
    etat, isAvoir: false, commentaire: '',
    tiersId: TIERS_BOULANG, tiers: tiers[1],
    commandeClientId: CC_001, commandeClient: null,
    adresseId: ADR_BOULANG, adresse: adresses[1],
    ribId: null, rib: null,
    bonLivraisons: [_buildTutoBL(true)],
    lignes: [{
      id: 'fl-tuto-1', factureId: FACT_001,
      commandeClientLigneId: CCL_001,
      quantite: 100, quantiteArticle: 100, prix: 12.5, tva: 5.5,
      lotId: LOT_BISCUITS, label: 'Biscuits sables du Moulin', commentaire: '',
      expeditions: [], isActive: true,
    }],
    paiements: [],
    totalHT: ht, totalTVA: tva, totalTTC: +(ht + tva).toFixed(2),
    tva: [{ tva: 5.5, montantTVA: tva }],
    isActive: true, version: 1,
  };
}

// ---------------------------------------------------------------------------
// Tuto planification des composants — stocks + événements prévisionnels
// ---------------------------------------------------------------------------

const _planifDay = (offset: number) =>
  new Date(_now.getFullYear(), _now.getMonth(), _now.getDate() + offset).toISOString();

/** Stock courant par article (GET /stock). */
export function _buildTutoStockForArticle(): any[] {
  return [
    { id: 'sfa-farine', articleId: ART_FARINE, quantity: 500 },
    { id: 'sfa-beurre', articleId: ART_BEURRE, quantity: 80 },
    { id: 'sfa-sucre', articleId: ART_SUCRE, quantity: 300 },
    { id: 'sfa-oeufs', articleId: ART_OEUFS, quantity: 360 },
    { id: 'sfa-lait', articleId: ART_LAIT, quantity: 200 },
    { id: 'sfa-biscuits', articleId: ART_BISCUITS, quantity: 250 },
  ];
}

/**
 * Événements prévisionnels (GET /planification/planification-composant-events).
 * Scénario pédagogique : la farine est consommée par un OF (sortie) et passe
 * sous son seuil de réapprovisionnement, puis une commande fournisseur la
 * réapprovisionne. Types : C (commande fourn, entrée J+1), OF_PROD (entrée
 * J+1), CC (expédition, sortie), OF_CONSO (conso, sortie).
 */
export function _buildTutoPlanifEvents(): any[] {
  return [
    {
      id: 'pe-farine', denomination: 'Farine de ble T65', articleId: ART_FARINE,
      events: [
        { elementId: OF_BISCUITS, date: _planifDay(3), quantite: 300, type: 'OF_CONSO' },
        { elementId: CF_001, date: _planifDay(10), quantite: 500, type: 'C' },
      ],
    },
    {
      id: 'pe-beurre', denomination: 'Beurre doux 82%', articleId: ART_BEURRE,
      events: [
        { elementId: OF_BISCUITS, date: _planifDay(3), quantite: 40, type: 'OF_CONSO' },
      ],
    },
    {
      id: 'pe-sucre', denomination: 'Sucre cristal', articleId: ART_SUCRE,
      events: [
        { elementId: OF_BISCUITS, date: _planifDay(3), quantite: 60, type: 'OF_CONSO' },
      ],
    },
    {
      id: 'pe-biscuits', denomination: 'Biscuits sables du Moulin', articleId: ART_BISCUITS,
      events: [
        { elementId: OF_BISCUITS, date: _planifDay(4), quantite: 250, type: 'OF_PROD' },
        { elementId: CC_001, date: _planifDay(2), quantite: 100, type: 'CC' },
      ],
    },
  ];
}
